import { Component, OnInit, OnDestroy, inject, Input, Output, EventEmitter, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  ConversionAuditTrail,
  DeleteConversionTarget,
  MbrsStatusEnum,
} from '../../../../types/commons';
import { DeleteConversionComponent } from '../../common/delete-conversion/delete-conversion.component';
import { FilingActionButtonsComponent } from '../../common/filing-action-buttons/filing-action-buttons.component';
import { ConversionEnginePickerComponent } from '../../common/conversion-engine-picker/conversion-engine-picker.component';
import { FilingInfoConfirmationDialogComponent } from '../../common/filing-info-confirmation-dialog/filing-info-confirmation-dialog.component';
import { FilingModeService } from '../../../services/mbrs/filing-mode.service';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { applyInputFilter, onPasteFilter as applyPasteFilter, applyTrimNormalizersToForm } from '../../../utils/input-filter';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
import { isHeldDisabledByAnotherRule } from '../../../utils/control-lock-state.util';
import { handleDateInputChange, handleDatePaste } from '../../../utils/helpers';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult, WarningRule } from '../../../models/validation-warning.model';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningMessageComponent, WarningMessage } from '../../common/warning-message/warning-message.component';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import {
  checkCurrentEndFloor,
  checkCurrentRange,
  checkDateSequence,
  checkPreviousRange,
  FILING_INFO_CONDITIONAL_RULES,
  FILING_INFO_FIELD_KEYS,
  isBeforeSupportedFinancialYearEnd,
  isUnconditionallyRequired,
  MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END,
  UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE,
} from '../../../services/mbrs/validators/filing-info-common.rules';
import {
  FILING_INFO_DRIVER_CLEAR_EDGES,
  isClearedValue,
  resolveDriverClearTargets,
} from '../../../services/mbrs/validators/filing-info-driver-clear.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
  TOAST_DURATION_SHORT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { coerceSchemaDateStrings } from '../../../utils/date-string-coercion.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    placeholder?: string;
    enumSource?: string;
    readonly?: boolean;
    defaultValue?: string;
    static?: string[];
    maxLength?: number; // Max chars enforced at the keystroke level on text inputs
    inputFilter?: string; // Keystroke/paste sanitiser key (e.g. "numeric") — see utils/input-filter
    trimLeadingZeros?: boolean; // Strip leading zeros as the user types/pastes — see utils/input-filter
    hidden?: boolean; // Hide field from display
    autoManageByTypeOfSubmission?: boolean; // Auto-manage visibility and value based on Type of Submission
    autoManageByContext?: boolean; // Auto-manage visibility based on multi-field context (status, type, standards, submission)
    disabledWhen?: {
      field: string;
      values: string[];
      setValueTo?: string; // Optional: Set field to this value when disabled
      hideWhenDisabled?: boolean; // Optional: Hide field when disabled
    };
    lockWhen?: {
      field: string;
      equals: string;
      setValueTo: string;
    };
    optionsFilteredBy?: {
      field?: string; // Single field (backward compatibility)
      fields?: string[]; // Multiple fields for composite key filtering
      mappingSource?: string; // Reference to typeDict mapping (e.g., "conditional:TypeOfCompanyByStatus")
      mappings?: { [key: string]: string[] }; // Inline mappings (backward compatibility)
    };
  };
  required?: boolean;
  requiredIf?: { qname: string; equals: string };
  requiredIfAny?: Array<{ qname: string; equals: string }>;
  // Per-field custom error copy from role-1.form.json (e.g. OriginOfCompany's
  // "Please enter origin of company."). Surfaced via getErrorMessage so role1
  // shows the same schema-driven messages as role0 instead of a generic
  // "<label> is required".
  error?: { required?: string; pattern?: string };
  validators?: Array<{ name: string; patternSource?: string; other?: string }>;
  triggers?: Array<{
    on: string;
    ifEquals: string;
    then: Array<{ action: string; targets: string[] }>;
  }>;
}

/**
 * Role-1 Filing Info component — Phase 2 editable filing-info surface.
 *
 * Renders a 27-field subset of role0's Phase 1 superset. The filing-form
 * container's sheet-selector filter reveals role0 in Phase 1 and role1 in
 * Phase 2 (one at a time, never both). Downstream sheets that inherit
 * filing-info values read via `FilingFormStateService.getActiveFilingInfoData()`,
 * which routes to `filingPrerequisites` in Phase 1 and `filingInfo` in
 * Phase 2 — so whichever sheet is editable, its values flow through.
 *
 * Field partition:
 *   - 24 visible filing-info fields (#1–#24 in `docs/sheet-specs/01_filing-info.md`).
 *   - 3 hidden reserved-seam fields (CompanyRegistrationNumber old format,
 *     FormerNameOfCompany, CourtOrderReferenceNumber).
 *   - 9 role0 fields (#25–#33: SharesTraded, BNM, Exemption, 5 method fields,
 *     ComparativeRestated) are delegated to role2 in Phase 2 — see
 *     `docs/sheet-specs/02_scope-of-filing.md` and the `_archived.delegatedToRole2`
 *     block in `role-1.form.json`.
 *
 * Business rules live in the shared `filing-info-common.rules.ts` module —
 * role0 and role1 consume the same conditional-required + date-sequence
 * logic. Do NOT re-implement rules here; modify the shared module so both
 * paths stay in sync.
 */
@Component({
  selector: 'app-role1-010000-filing-info',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    DatePicker,
    TooltipModule,
    Toast,
    ConfirmDialogModule,
    WarningMessageComponent,
    DeleteConversionComponent,
    FilingActionButtonsComponent,
    ConversionEnginePickerComponent,
    FilingInfoConfirmationDialogComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './role1-010000-filing-info.component.html',
  styleUrls: ['./role1-010000-filing-info.component.css'],
})
export class Role1010000FilingInfoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private confirmationService = inject(ConfirmationService);
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);
  private filingModeService = inject(FilingModeService);
  private router = inject(Router);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  // Inputs from parent filing-form-container for delete-conversion + action buttons
  @Input() conversionId: string | null = null;
  @Input() companyName: string = '';
  @Input() auditYear: number = 0;
  @Input() conversionStatus: number | null = null;
  /** Created/modified trail for the delete dialog; `null` until the container's `xbrl-data-listing` read answers. */
  @Input() conversionAudit: ConversionAuditTrail | null = null;
  // Developer Note: role1 deliberately has NO `isOnlyFilingSheet` input. Its
  // Submit gate blocks on validity and on pristine with NO waiver, so the flag
  // has no reader — and a bound-but-ignored input reads as a live dependency.
  // Do not add one to waive the pristine term the way role0 does: the
  // container clamps `filteredSheets` to a single sheet once the gate sheet
  // reports `hasErrors`, which would flip the flag true exactly when Submit
  // must stay disabled. role0 takes the input because Phase 1 genuinely needs
  // the waiver for a fresh, valid-by-defaults, untouched conversion.
  //
  // Since 2026-08-24 that clamp is Phase-1-only — Phase 2 no longer clamps on
  // validity (see the VALIDITY clamp in SheetApplicabilityService). role0, the
  // only reader of the flag, renders only in Phase 1, so the reasoning is
  // unchanged; adding the input here would still be wrong the moment the
  // Phase-2 clamp were ever reinstated.

  /**
   * The engine that last processed this conversion, passed down by
   * `FilingFormContainerComponent`. Null when the backend has not recorded one.
   */
  @Input() lastUsedEngine: string | null = null;

  // Event emitter for submit action — parent (container) processes the payload
  @Output() submitForm = new EventEmitter<any>();

  /**
   * "Are there unsubmitted changes on THIS sheet?" — the Submit gate's
   * changes-term, and the same modified-vs-original verdict that drives the
   * sheet selector's pencil (`isSheetModified`).
   *
   * Deliberately NOT `filingForm.pristine`. Angular's flag answers a different
   * question ("has the user touched this form since it mounted?") and diverges
   * from the user's own mental model of "changes" in both directions:
   *
   *   - Edit a field, then type the ORIGINAL value back: pristine stays false,
   *     so a pristine-gated Submit stays enabled with nothing to submit. The
   *     P.O. ruled on 2026-08-17 that Submit must re-gray here.
   *   - Edit Filing Info, switch sheets, come back: the container's `*ngIf`
   *     DESTROYS this component, so the remounted form is pristine again while
   *     the edits are still unsubmitted — a pristine-gated Submit would be dead
   *     with real work pending, contradicting the lit pencil beside it.
   *
   * `isSheetModified` survives the remount (it compares the persisted modified
   * slice against original, normalising dates and skipping calculated keys), so
   * it is right in both cases.
   *
   * Kept as a signal refreshed from state rather than called straight from the
   * template: the comparison is a deep compare over every field, and a template
   * binding would re-run it on every change-detection tick.
   *
   * Cost, accepted: the refresh rides the autosave, so after a genuine edit
   * Submit enables one FORM_AUTOSAVE_DEBOUNCE_MS later. That is the
   * safe direction to be wrong — briefly disabled while the user is still
   * typing, never briefly enabled with nothing to submit. Note role1's
   * IMMEDIATE pencil subscriber in `setupStatePersistence` cannot be reused
   * here: it is one-way (`markSheetAsEdited`), so it would light Submit on a
   * revert keystroke for one debounce — exactly the flicker the P.O. asked us
   * to remove.
   */
  hasUnsubmittedChanges = signal<boolean>(false);

  /**
   * Engine chosen for the Phase 2 re-run this sheet's Submit triggers. Read by
   * the container in `onRole1Submit`; null means "omit the field".
   *
   * Component-local by decision — the container's `*ngIf` destroys this sheet
   * on every sheet switch, so a pick does not survive navigating away and back.
   * Accepted and communicated to QA. See ConversionEnginePickerComponent.
   */
  selectedEngine: string | null = null;

  onEngineChange(engine: string | null): void {
    this.selectedEngine = engine;
  }

  /**
   * A Phase 2 conversion only re-runs when Filing Info actually changed, so
   * the picker is dead until then.
   *
   * This is the SAME term Submit and Cancel gate on — `hasUnsubmittedChanges()`,
   * the modified-vs-original verdict that also drives the pencil — so role 1
   * keeps ONE modified-verdict rather than two that can disagree. It is
   * deliberately NOT `filingForm.pristine`: pristine is wrong in both
   * directions here (it stays dirty after a hand-revert, and it resets to clean
   * when the container's `*ngIf` remounts this sheet with edits still pending).
   * Read the comment above `app-filing-action-buttons` in the template before
   * changing it. Pinned in `filing-info-submit-gate-parity.spec.ts`.
   */
  isEnginePickerDisabled(): boolean {
    return !this.hasUnsubmittedChanges();
  }

  // Dialog states
  isArchiveDialogOpen = signal<boolean>(false);
  isEditWarningDialogOpen = signal<boolean>(false);

  // Track if Role-1 was initially completed (to show edit warning on re-submit)
  private wasInitiallyCompleted = false;
  private hasShownEditWarning = false;
  /** True while the container's phase-two submit is in flight. Drives the
   * Submit-button spinner and keeps the commit deferred until success
   * (see onSubmitSucceeded / onSubmitFailed). */
  isSubmitting = false;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  filingForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Filtered dropdown options (for conditional filtering)
  filteredDropdownOptions: { [key: string]: any[] } = {};

  // Track which fields should be hidden
  hiddenFields: { [key: string]: boolean } = {};

  /**
   * Apply-callbacks registered by the reactive-rule engines below. Re-invoked
   * after an API hydration patch, which uses `emitEvent: false` and therefore
   * never wakes those engines' own `valueChanges` subscribers.
   *
   * Generalises the existing `reapplyConditionalValidation()` — same problem,
   * same shape, six rule families wider. Registration ORDER is the dependency
   * order (see reapplyReactiveRules), matching role0's identical mechanism.
   */
  private reactiveRuleAppliers: Array<() => void> = [];

  /**
   * Re-entrancy guard for `setupDriverClearCascade`. The cascade clears its
   * targets with `emitEvent: true` (so the TypeOfSubmission auto-set seam
   * fires), and a cleared target is itself a driver — which re-enters the same
   * subscriber. The closure is already complete on the first pass, so the
   * re-entrant passes are collapsed rather than repeated.
   */
  private isDriverClearCascading = false;

  /**
   * Re-entrancy guard for the `setupLockWhenRules` re-assert subscriber
   * (Product ruling 2026-08-10). That subscriber writes `setValueTo` back
   * onto the target the instant it observes an empty value while the
   * trigger still matches the locking condition — and since `setValueTo` is
   * itself written back onto the target, the write re-enters the same
   * `targetControl.valueChanges` subscriber. `setValueTo` is always a
   * non-empty literal, so the emptiness check alone would already stop the
   * second pass — this flag is a belt-and-braces guard, not the only line
   * of defence. Mirrors role0's identical field — keep both in sync.
   */
  private isLockWhenReasserting = false;

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  /**
   * Whether this sheet already had unsubmitted changes at the moment
   * `loadSavedState()` ran — captured BEFORE `markSheetInitializing()` opens the
   * initializing bracket.
   *
   * Why a snapshot is required: `FilingFormStateService.isSheetModified()`
   * short-circuits to `false` for any sheet inside `initializingSheets`, and that
   * bracket is opened by `loadSavedState()` and closed only by `finalizeInit()`.
   * `setupStateListener()` is registered between the two, and its `originalState$`
   * subscription fires SYNCHRONOUSLY on subscribe — so a live `isSheetModified()`
   * read from that subscriber is always `false` during the mount pass and cannot
   * protect anything. Same class of trap `refreshHasUnsubmittedChanges()` exists
   * for.
   *
   * Captured inside `loadSavedState()` rather than `ngOnInit` so that cancel /
   * revert — which re-calls `loadSavedState()` — refreshes it, letting a
   * genuinely reverted sheet accept backend values again.
   *
   * Known, accepted staleness window: if the user HAND-reverts every field
   * instead of using Cancel/Revert, nothing re-calls `loadSavedState()`, so this
   * stays `true` and a later `loadFromAPI()` baseline emission is suppressed for
   * a sheet that is no longer modified. Bounded and fail-safe — the form already
   * holds values equal to the original slice, the reapply hooks still run, and
   * role1 gates nothing on `filingForm.pristine` (the submit gate and Cancel both
   * read the state-derived `hasUnsubmittedChanges()`). It errs toward preserving
   * the user's work, which is the direction this guard exists to protect.
   */
  private wasModifiedAtLoad = false;

  // Store custom cross-field date validator functions for reuse — so they can
  // be re-applied after setupConditionalValidation() rewrites a control's
  // validator list (mirrors role0's dateValidators map).
  private dateValidators: { [key: string]: any } = {};

  /**
   * Registration order note (2026-08-10): role1 wires its reactive-rule
   * engines disable -> lock -> condOptions -> bankNegara -> autoset ->
   * methods, which is a DIFFERENT relative order from role0's disable ->
   * condOptions -> bankNegara -> lock -> autoset -> methods. This is
   * harmless today — the only load-bearing constraint across both sheets is
   * lock-before-autoset, which holds in both orderings, and Basis carries no
   * `optionsFilteredBy` for condOptions to race against. Keep this in mind
   * before adding a new engine on either sheet: verify it against the
   * ordering below, not against the other sheet's.
   */
  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '1',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      // Step 1 is marked only AFTER the wait, like every other sheet. It used to
      // be marked on arrival on the claim that this sheet never waits, but
      // `finalizeSheetInit` awaits the same flag, so a hydration hang reported
      // `steps reached: [1,…]` instead of the designed `[]`. Awaiting first puts
      // a stall at the first step; the schema fetch now starts after it (static,
      // cached asset, so the cost is small).
      await this.stateService.waitForMbrsHydration('role1');
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);

      // Check if Role-1 was initially completed (to gate the edit-warning
      // dialog so it only fires for re-submission of an already-completed
      // filing-info form, mirroring role0's wasInitiallyCompleted flag).
      this.wasInitiallyCompleted = this.isRole1Completed();

      this.setupStatePersistence();
      this.setupUnsubmittedChangesTracking();
      this.setupStateListener();
      this.setupConditionalValidation();
      this.setupFinancialYearDateValidation();
      this.setupFieldDisabling();
      // Keep BEFORE setupTypeOfSubmissionAutoSet (2026-08-10, since the
      // `deriveTypeOfSubmissionFromBasis` deletion in `applyLockRule` — see
      // that method's docblock). A lock application that fires during THIS
      // initial pass (a filing that mounts with Origin already at the
      // locking value) is a SYSTEM application (`emitEvent: false` — FIX 1,
      // product ruling that a page load with no user action must not arm the
      // pencil), so it derives Type of submission NOT via an emission but
      // because `setupTypeOfSubmissionAutoSet`'s own mount-time call
      // independently re-reads Basis's already-locked value when it runs
      // second. This registration order IS LOAD-BEARING: if
      // `setupTypeOfSubmissionAutoSet` ran FIRST instead, ITS mount-time call
      // would simply read Basis's still-unlocked value, and there is no
      // later re-derivation to correct it — so this order matters for SYSTEM
      // applications, unlike the user-driven case where the emission reaches
      // whichever subscriber is already registered either way. See the
      // "ngOnInit order" describe in `role1-driver-clear-cascade.spec.ts` for
      // both directions pinned (the regression guard explicitly asserts that
      // reversed order breaks), and its docblock for why this order must be
      // preserved rather than treated as interchangeable — a future lockWhen
      // target or engine may not have that same redundancy.
      this.setupLockWhenRules();
      this.setupConditionalOptions();
      this.setupBankNegaraFieldManagement();
      // Developer Note [Not supported in role1]: KFI-MFRS / KFI-MPERS are
      // out of supported scope — TypeOfSubmission is constrained to
      // FS-MFRS / FS-MPERS upstream. The KFI exemption-management helper
      // targets a field (`ssmt:Description…Exemption…`) that isn't in
      // role1's 27-field schema either (it's delegated to role2), so this
      // call would early-return via the `control not found` guard even
      // if the branch were reachable. Re-enable this call (and the method
      // below) only if KFI variants are ever adopted for role1.
      // this.setupKFIMFRSExemptionFieldManagement();

      // MUST run before setupMethodFieldsManagement(): that manager's composite
      // key reads `ssmt-dei:TypeOfSubmission` live, so the derived half has to
      // be settled before its Basis subscriber fires (same ordering as role0).
      this.setupTypeOfSubmissionAutoSet();
      // MUST be registered AFTER setupTypeOfSubmissionAutoSet: both subscribe
      // to Basis's own valueChanges, and this one needs to be the LAST
      // subscriber on that control to avoid a stale re-entrant emission. See
      // the docblock on setupLockWhenReassert for the measured failure mode.
      this.setupLockWhenReassert();
      this.setupMethodFieldsManagement();
      this.setupWarningEvaluation(); // Setup warning evaluation for all fields
      this.evaluateFieldWarnings(); // Evaluate warnings on initial load

      // MUST be registered LAST: subscribers fire in registration order, and
      // this one clears values that earlier subscribers (lockWhen re-enable,
      // option re-filtering, method-field management) have just re-evaluated.
      this.setupDriverClearCascade();

      // Deterministic finalization (replaces the historical chained
      // setTimeout(600) → setTimeout(600) inside loadSavedState). See
      // `finalizeInit()`'s docblock for the readiness contract and why no
      // `awaitSettled` is needed (TIER-A) — same as role0.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
    catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    // Parity with role0/Filing Prerequisite: listen to `originalState$` (the API-load channel),
    // NOT `state$` (modifiedState$, which re-emits on every autosave). Backend
    // hydration for filingInfo flows through `loadFromAPI()`, which writes
    // originalState$ on every load and modifiedState$ only for sheets with no
    // unsubmitted edits (changed 2026-08-17 so opening a second tab cannot
    // destroy a sibling's work). originalState$ therefore still carries the
    // hydrated values, once per API load — but it may now DISAGREE with the
    // modified slice, which is why the subscriber below guards its patch on
    // `isSheetModified`. Listening to modifiedState$
    // here would re-fire on each debounced autosave and (with the markAsPristine
    // below) wrongly reset the user's dirty form back to pristine — the feedback
    // loop role0 documents.
    //
    // NOTE: kept SYNCHRONOUS (no setTimeout). role1 deliberately removed the
    // historical setTimeout padding because `patchValue(..., { emitEvent: false })`
    // does NOT emit valueChanges/statusChanges, so the autosave subscriber cannot
    // wake as a side-effect of the patch — that reasoning is independent of which
    // state stream feeds this listener, so the cleaner synchronous finalization is
    // preserved.
    this.stateService.originalState$
      .pipe(
        map((state) => state.filingInfo),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ), // Only react to actual changes
        takeUntil(this.destroy$)
      )
      .subscribe((filingData) => {
        if (filingData && Object.keys(filingData).length > 0) {
          // Guard auto-save during the external-data patch (synchronous — see
          // the no-setTimeout note above).
          this.isLoadingState = true;

          // Convert date strings to Date objects (same as loadSavedState does)
          const convertedData =
            this.convertDateStringsToDateObjects(filingData);

          // Preserve default values for readonly fields that are empty in the
          // external data, so an empty API value never wipes a schema default
          // (mirrors role0's listener).
          const finalData: Record<string, any> = { ...convertedData };
          this.schema.controls.forEach((control) => {
            if (control.ui.readonly && control.ui.defaultValue) {
              const apiValue = finalData[control.qname];
              if (apiValue === undefined || apiValue === null || apiValue === '') {
                finalData[control.qname] = control.ui.defaultValue;
              }
            }
          });

          // Skip the DATA WRITE when this sheet has unsubmitted changes.
          //
          // `loadSavedState()` has already patched the form from the MODIFIED
          // slice, which on a cold mount carries a sibling tab's unsubmitted
          // edits (IndexedDB is shared, and `loadFromAPI()` has left the modified
          // slice alone for edited sheets since 2026-08-17). Writing the baseline
          // over those values would make a newly opened tab silently disagree
          // with the tab holding the work. Matches the contract `loadFromAPI()`
          // and `snapshotAllBaselines()` already enforce: edited sheets win.
          //
          // `markAsPristine()` is skipped WITH the patch, never independently —
          // either half alone is worse than doing neither.
          //
          // NOTE: the two reapply calls below stay OUTSIDE this guard. See their
          // own comments for why.
          //
          // BOTH terms are load-bearing. `wasModifiedAtLoad` is the snapshot taken
          // before `loadSavedState()` opened the `initializingSheets` bracket — it
          // is the only truthful answer available during the SYNCHRONOUS mount-pass
          // replay of this BehaviorSubject, because a live `isSheetModified()` read
          // inside that bracket always returns `false`. The live read covers the
          // later emissions — a `loadFromAPI()` refresh arriving after
          // `finalizeInit()` has closed the bracket, by which time the user may have
          // edited. See `wasModifiedAtLoad`'s docblock.
          if (
            !this.wasModifiedAtLoad &&
            !this.stateService.isSheetModified(SHEET_KEYS.filingInfo)
          ) {
            this.filingForm.patchValue(finalData, { emitEvent: false });

            // Mark pristine: this is external API data, not user edits, so the
            // form must not read as dirty after hydration (B7 parity with role0).
            this.filingForm.markAsPristine();
          }

          // DELIBERATELY OUTSIDE the isSheetModified guard above. These two
          // methods have exactly one call site each — this listener — and
          // setupConditionalValidation() only WIRES valueChanges subscriptions
          // rather than applying rules to values already in the form. Folding
          // them into an early-return would leave every cold-mounted dirty tab
          // with correct values but un-applied rules, and a submit gate that
          // silently disagrees with the other tab. Pinned by
          // `role1-cold-mount-hydration-precedence.spec.ts`.
          //
          // Re-apply conditional-required rules for the JUST-HYDRATED trigger
          // values. The patchValue above used `emitEvent: false`, so the
          // valueChanges-driven wiring in setupConditionalValidation never saw
          // the backend value — without this, a conversion that already had
          // "Subsequent preparation of financial statements" set would load
          // with its previous-year date fields un-required (no error, no submit
          // block). See reapplyConditionalValidation() for the full rationale.
          this.reapplyConditionalValidation();

          // Re-apply the reactive-rule engines for the JUST-HYDRATED values.
          // Same rationale as reapplyConditionalValidation above, one rule
          // family wider: the patch used `emitEvent: false`, so disabledWhen /
          // lockWhen / optionsFilteredBy / TypeOfSubmission / BankNegara /
          // method-fields never saw the backend value. Without this, an
          // extracted conversion renders enable/disable, hidden and filtered-
          // option state computed from PRE-hydration values.
          this.reapplyReactiveRules();

          // Surface validation state for hydrated data: mark invalid, enabled
          // controls touched so red borders + messages show immediately
          // (markAsTouched does not emit valueChanges, so it cannot wake
          // autosave). Mirrors role0 and role1's own on-load touch loop.
          Object.keys(this.filingForm.controls).forEach((key) => {
            const control = this.filingForm.get(key);
            if (control && control.invalid && !control.disabled) {
              control.markAsTouched();
            }
          });

          this.isLoadingState = false;
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-1.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach((key) => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map(
        (value: string) => ({
          label: value,
          value: value,
        })
      );
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator.
      //
      // A field is unconditionally required when EITHER the top-level
      // `control.required === true` flag is set OR the validators array
      // contains a `{ name: 'required' }` entry without a conditionalOn
      // property. The second form was historically silently dropped (the
      // pre-fix code only read the top-level flag), which meant schema
      // entries like `ssmt-dei:DescriptionOfFunctionalCurrency` that carried
      // `required: false` plus `validators: [{ name: 'required' }]` were
      // never enforced. This derivation now lives in the shared
      // filing-info-common.rules so this editor, the pure validator, and the
      // role0 paths apply the identical rule — keep them aligned by editing
      // the shared helper, not here.
      if (isUnconditionallyRequired(control)) {
        validators.push(Validators.required);
      }

      // Add pattern validator if specified
      const patternValidator = control.validators?.find(
        (validator) => validator.name === 'pattern'
      );
      if (patternValidator && patternValidator.patternSource) {
        const patternKey = patternValidator.patternSource.replace('type:', '');
        const pattern = this.typeDict.patterns?.[patternKey];
        if (pattern) {
          validators.push(Validators.pattern(pattern));
        }
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.filingForm = new FormGroup(formControls);
  }

  /**
   * Load saved state from state service
   */
  /**
   * Patch the form with whatever is currently in the FilingFormState slot for
   * `filingInfo`. Lifecycle responsibilities (clearing `isLoadingState`,
   * snapshotting the validation baseline via `markSheetInitializedWithStatus`)
   * are owned by `finalizeInit()` — see that method's docblock for the
   * deterministic readiness contract that replaced the historical
   * `setTimeout(600) → setTimeout(600)` pair.
   */
  private loadSavedState(): void {
    // MUST be read BEFORE markSheetInitializing() below — see wasModifiedAtLoad.
    this.wasModifiedAtLoad = this.stateService.isSheetModified(
      SHEET_KEYS.filingInfo
    );
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.filingInfo);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.filingInfo);

    if (savedData && Object.keys(savedData).length > 0) {
      // Convert date strings back to Date objects for date picker fields
      const convertedData = this.convertDateStringsToDateObjects(savedData);
      this.filingForm.patchValue(convertedData, { emitEvent: false });
    }
  }

  /**
   * Deterministic init finalization (mirrors role0's `finalizeInit()` —
   * see `role0-000000-filing-prerequisites.component.ts` for the template
   * this was adopted from, and `sheet-init-finalize.util.ts` for the shared
   * helper both sheets now share).
   *
   * Must run after EVERY `loadSavedState()` — init AND cancel/revert —
   * because `loadSavedState()` opens the bracket with
   * `markSheetInitializing()` and only this closes it. A sheet left inside
   * `initializingSheets` never reports status.
   *
   * TIER-A: no `awaitSettled` is passed. Every init-time mutation writes
   * with `emitEvent: false` or goes through `syncFormToState()`, so nothing
   * is left to settle before the readiness snapshot — see the removed
   * `ngOnInit` comment block (now folded into this docblock) for the full
   * four-point readiness contract this replaces.
   *
   * Unlike role0, `persistInitial` is deliberately OMITTED here. This is NOT
   * because role1 alone calls `syncFormToState()` — role0 does too, on every
   * one of its reactive-rule engines (`applyLockRule`, the method-fields
   * engine as of 2026-08-10, the driver-clear cascade). The real divergence
   * is that role0's mount path also has a plain, uncompensated
   * `patchValue(..., { emitEvent: false })` for readonly-field schema
   * defaults (the "no saved data" branch in `loadSavedState()`) that goes
   * through none of those engines and calls no `syncFormToState()` of its
   * own — `persistInitial` is what re-baselines that write into
   * `modifiedStateSubject`. role1 has no equivalent uncompensated mount-time
   * write: every one of its init-time mutations is already covered by a
   * `syncFormToState()` call from the engine that made it, so an extra
   * re-baseline here would be redundant.
   *
   * The touch loop runs AFTER the helper: it marks invalid controls
   * touched so red borders show, and `touched` does not affect
   * `form.valid`, which is all `markSheetInitializedWithStatus` reads.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.filingInfo,
      form: this.filingForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
    });

    this.touchInvalidControls();

    // The initializing bracket has just closed, so isSheetModified() can now
    // answer truthfully — see refreshHasUnsubmittedChanges() for why this call
    // cannot be left to the state subscription alone.
    this.refreshHasUnsubmittedChanges();
  }

  /** Mark invalid, enabled controls touched so validation errors paint. */
  private touchInvalidControls(): void {
    Object.keys(this.filingForm.controls).forEach((key) => {
      const control = this.filingForm.get(key);
      if (control && control.invalid && !control.disabled) {
        control.markAsTouched();
      }
    });
  }

  /**
   * Convert date strings from persisted/API state back to Date objects.
   *
   * Delegates to the shared `coerceSchemaDateStrings` — see that util's
   * docblock for why an unconverted string renders as a BLANK
   * `<p-datepicker>`, and for which state slice carries which shape. role0
   * and role2 carry the identical one-liner; keep the three in step.
   */
  private convertDateStringsToDateObjects(
    data: Record<string, any>
  ): Record<string, any> {
    return coerceSchemaDateStrings(data, this.schema?.controls);
  }

  /**
   * Setup auto-save on form changes
   * Only saves to localStorage when form is VALID
   *
   * Two parallel subscriptions on `filingForm.valueChanges`:
   *
   *   1. Immediate (no debounce) — flips the `editedSubject` flag on the
   *      state service so the sheet selector's tick/cross swaps to the
   *      pencil icon as soon as the user touches a field, without waiting
   *      for the debounce window. This is a responsiveness nicety: the
   *      debounced path (#2) also raises the pencil, just one debounce
   *      later.
   *
   *      It stays a plain `markSheetAsEdited` (one-way) on purpose — it
   *      only ever needs to SET the pencil for instant feedback. Clearing
   *      it on a revert-to-original is owned by the debounced path, which
   *      calls `updateSheetData` → `reconcileEditedFlag` (modified-vs-
   *      original). So a revert keystroke may light the pencil for one
   *      debounce window here before #2 reconciles it back off — matching
   *      the "pencil shows while editing" behaviour of every other sheet.
   *
   *      Programmatic writes from the setup methods use
   *      `setValue(..., { emitEvent: false })` and therefore never
   *      reach either subscriber, so this immediate handler does NOT
   *      spuriously flip the flag during cascaded rule application.
   *
   *   2. Debounced (FORM_AUTOSAVE_DEBOUNCE_MS) — owns state
   *      persistence + sheet-level validation status. Its
   *      `updateSheetData` call reconciles the pencil against original,
   *      so reverting every field clears the icon.
   */
  /**
   * Recompute the Submit gate's changes-term from state.
   *
   * Must be called from `finalizeInit()` as well as from the state
   * subscription: `isSheetModified` returns false for a sheet inside the
   * `initializingSheets` bracket, and `finalizeSheetInit` closes that bracket
   * WITHOUT emitting on `modifiedState$` — so a remount of a genuinely dirty
   * sheet would otherwise sit at `false` until the user's next keystroke.
   */
  private refreshHasUnsubmittedChanges(): void {
    this.hasUnsubmittedChanges.set(
      this.stateService.isSheetModified(SHEET_KEYS.filingInfo)
    );
  }

  /**
   * Keep `hasUnsubmittedChanges` in step with the dual-state store.
   *
   * Watches BOTH subjects, because both move it:
   *   - `modifiedState$` — the debounced autosave writing the user's edits.
   *   - `originalState$` — `commitSheetAsOriginal()` on a successful submit,
   *     which re-baselines the sheet and must re-gray Submit immediately.
   */
  private setupUnsubmittedChangesTracking(): void {
    combineLatest([this.stateService.modifiedState$, this.stateService.originalState$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshHasUnsubmittedChanges());
  }

  private setupStatePersistence(): void {
    this.filingForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.isLoadingState &&
          !this.stateService.isSheetInitializing(SHEET_KEYS.filingInfo)
        ) {
          this.stateService.markSheetAsEdited(SHEET_KEYS.filingInfo);
        }
      });

    buildFormEditPipeline({
      source$: this.filingForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        // Normalise the form BEFORE reading its value or validity — mirrors
        // role0. Without this the form keeps the untrimmed BRN while state
        // holds the trimmed one, so `isValid` and the Submit gate are computed
        // against a value that is not the one being submitted. See
        // applyTrimNormalizersToForm.
        applyTrimNormalizersToForm(this.filingForm);

        const formValue = this.filingForm.getRawValue();
        const isValid = this.filingForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.filingInfo, formValue, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.filingInfo, { hasErrors: !this.filingForm.valid });
      },
    });
  }

  /**
   * Push the form's current `getRawValue()` snapshot into FilingFormStateService
   * without going through the user-edit auto-save path.
   *
   * Why this exists:
   *   The setup methods (`setupFieldDisabling`, `setupBankNegaraFieldManagement`,
   *   `setupMethodFieldsManagement`, `setupLockWhenRules`, `setupConditionalOptions`)
   *   programmatically call `setValue(..., { emitEvent: false })` to
   *   apply schema-driven side effects (lock-to-default, hide-and-clear,
   *   FS-EPC method blackout, etc.). The `emitEvent: false` flag is required
   *   so these mutations don't re-trigger the conditional/disabled subscribers
   *   they themselves react to (which would cause infinite loops and stale
   *   filtered-options dropdowns). But it ALSO suppresses
   *   `setupStatePersistence`'s `valueChanges` listener — meaning the
   *   form's disabled-field truth never reaches `modifiedStateSubject`
   *   until the next user-driven edit.
   *
   *   Pure validators read from `modifiedStateSubject`, while the mount-based
   *   path reads from `form.valid`. The drift between the two is what
   *   trips the dual-path parity check on the listing-return revisit.
   *   Calling this after every programmatic mutation keeps both views aligned.
   *
   *   `skipEditedMark = true` because these mutations are system-driven
   *   (schema rules), not user edits — the pencil icon and edited-state
   *   dirty tracking should remain owned by the user-driven valueChanges
   *   pipeline in `setupStatePersistence`.
   *
   *   Sheet-level status is intentionally NOT updated here. Status
   *   publication is owned by `markSheetInitializedWithStatus` (end of
   *   init) and `setupStatePersistence` (subsequent user edits). Pushing
   *   status from inside the schema-rule subscribers would multiply
   *   parity-check invocations without changing the outcome.
   *
   *   ⚠️ COPY-PASTE WARNING (icon-flicker trap):
   *   This helper writes to modifiedState with `skipEditedMark: true`, which
   *   correctly avoids the direct edited-flag flip — but it also creates a
   *   modified-vs-original *diff* (the schema-driven defaults aren't in the
   *   original cache). If a downstream listener (cross-tab sync rehydrate,
   *   `hydrateEditedStatusFromCaches`, validation cycle completion) re-runs
   *   `isSheetModified` after init, it will detect that diff and flip the
   *   sheet to the pencil icon — even though the user did nothing. Role1
   *   relies on `markSheetInitializing` / `markSheetInitialized` in
   *   `FilingFormStateService` to clear that spurious edit and snapshot the
   *   post-setup modified state as the new baseline. If you copy this
   *   pattern into another role, ensure both bracket calls are made AND
   *   that `markSheetInitialized` is invoked AFTER all setup writes (and
   *   `waitForMbrsHydration`) have completed — otherwise the flicker
   *   resurfaces. See filing-form-state.service.ts → editedAtInitStart.
   */
  private syncFormToState(): void {
    const formValue = this.filingForm.getRawValue();
    this.stateService.updateSheetData(SHEET_KEYS.filingInfo, formValue, this.filingForm.valid, true);
  }

  /**
   * User-initiated driver-clear cascade for the dropdown chain
   *   Origin of company -> Types of company -> Basis of accounting standards
   * (and, through the existing auto-set seam, -> Type of submission).
   *
   * The graph lives in filing-info-driver-clear.rules.ts and is shared with
   * role0 so the two sheets cannot disagree about what resets.
   *
   * THREE THINGS HERE ARE LOAD-BEARING:
   *
   * 1. `emitEvent: true`. Clearing Basis must reach
   *    `setupTypeOfSubmissionAutoSet`'s subscriber, which owns the derived
   *    FS-MFRS/FS-MPERS clear. Emitting also lets the option-filter and
   *    method-field engines re-evaluate against the new emptiness.
   *
   * 2. Registered last as a deliberate default: this subscriber's job is to
   *    OVERWRITE what earlier rules just computed, so running it after them
   *    requires no reasoning about their side effects. Verified 2026-08-10
   *    that position is not load-bearing TODAY — but only because every rule
   *    that writes a value into a cascade target (`lockWhen.equals`,
   *    `disabledWhen.values`) is keyed to a NON-EMPTY literal, so none can
   *    fire on the `''` this cascade writes. Add `""` to a
   *    `disabledWhen.values`, or a `setDefaultWhen` anywhere in the
   *    Origin -> Types of company -> Basis chain, and this must stay last.
   *    Pinned by `filing-info-driver-clear.schema-invariant.spec.ts`.
   *
   * 3. NOT registered in `reactiveRuleAppliers`. Hydration is a different rule:
   *    an extraction-NULL driver renders the dependent empty + DISABLED +
   *    unvalidated (see setupConditionalOptions). This cascade is user-only and
   *    leaves the dependents ENABLED and required, so the user can re-pick.
   *    Hydration patches use `emitEvent: false` and therefore never reach here.
   */
  private setupDriverClearCascade(): void {
    Object.keys(FILING_INFO_DRIVER_CLEAR_EDGES).forEach((driverQname) => {
      const driverControl = this.filingForm.get(driverQname);

      if (!driverControl) {
        console.warn(
          `driverClearCascade: driver control '${driverQname}' not found`
        );
        return;
      }

      driverControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (!isClearedValue(value) || this.isDriverClearCascading) {
            return;
          }

          this.isDriverClearCascading = true;
          try {
            resolveDriverClearTargets(driverQname).forEach((targetQname) => {
              const targetControl = this.filingForm.get(targetQname);

              if (!targetControl) {
                return;
              }

              // Always propagate, even when the target is ALREADY empty:
              // an already-empty Basis still needs its `emitEvent: true` to
              // reach setupTypeOfSubmissionAutoSet's subscriber, or a latched
              // FS-MFRS/FS-MPERS in Type of submission survives the cascade.
              // Only the dirty/touched marks are conditional — don't flag a
              // control the user never filled as if they just cleared it.
              const wasAlreadyEmpty = isClearedValue(targetControl.value);
              targetControl.setValue('', { emitEvent: true });
              if (!wasAlreadyEmpty) {
                targetControl.markAsDirty();
                targetControl.markAsTouched();
              }
            });
          } finally {
            this.isDriverClearCascading = false;
          }

          this.syncFormToState();
        });
    });
  }

  /**
   * Setup conditional field validation.
   *
   * Driven by the shared FILING_INFO_CONDITIONAL_RULES table so that the imperative
   * wiring here and the pure role1FilingInfoValidator consume the same rule
   * set. Each rule: when the trigger field's value is in `whenAnyOf`, every
   * field in `requireFields` becomes required; otherwise validators are
   * cleared and the value reset. See filing-info-common.rules.ts.
   */
  private setupConditionalValidation(): void {
    // Group rules by trigger so we subscribe once per trigger field.
    const rulesByTrigger = new Map<string, typeof FILING_INFO_CONDITIONAL_RULES>();
    for (const rule of FILING_INFO_CONDITIONAL_RULES) {
      const bucket = rulesByTrigger.get(rule.trigger) ?? [];
      (bucket as any[]).push(rule);
      rulesByTrigger.set(rule.trigger, bucket as typeof FILING_INFO_CONDITIONAL_RULES);
    }

    for (const [triggerQname, rules] of rulesByTrigger) {
      const triggerControl = this.filingForm.get(triggerQname);
      if (!triggerControl) continue;

      // Apply every rule for this trigger against a given value. Extracted so
      // it runs BOTH for the trigger's initial (mount-time) value AND on every
      // subsequent change.
      const applyRules = (value: unknown) => {
        for (const rule of rules) {
          const shouldBeRequired = rule.whenAnyOf.includes(value as string);
          for (const fieldQname of rule.requireFields) {
            this.updateConditionalField(fieldQname, shouldBeRequired);
          }
        }
      };

      // CRITICAL: apply once for the trigger's current value before
      // subscribing. The form is hydrated with `patchValue(..., { emitEvent:
      // false })` (loadSavedState, the originalState listener, cross-tab
      // patches), which does NOT fire valueChanges — so a conversion that
      // mounts with the trigger already set to "Subsequent preparation of
      // financial statements" would otherwise never attach Validators.required
      // to the previous-year date fields. Mirrors role0's
      // applyRule(triggerControl.value).
      applyRules(triggerControl.value);

      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => applyRules(value));
    }
  }

  /**
   * Re-apply every conditional-required rule against its trigger's CURRENT
   * value.
   *
   * Why this exists separately from setupConditionalValidation's mount-time
   * pass: backend hydration flows through the `originalState$` listener, which
   * patches the trigger field with `patchValue(..., { emitEvent: false })`.
   * That suppresses `valueChanges`, so the per-trigger subscription above never
   * sees the hydrated value, and setupConditionalValidation's one-shot
   * `applyRules(triggerControl.value)` already ran (before the API data
   * arrived) against the empty/stale value. The net effect was a regression:
   * a conversion that already had "Subsequent preparation of financial
   * statements" persisted on the backend would load with its previous-year
   * date fields NOT marked required — no error, no submission block — until the
   * user manually toggled the dropdown. Calling this right after the listener's
   * patch re-attaches Validators.required for the hydrated trigger value so the
   * subsequent touch-loop surfaces the empty-required errors immediately.
   */
  private reapplyConditionalValidation(): void {
    for (const rule of FILING_INFO_CONDITIONAL_RULES) {
      const triggerControl = this.filingForm.get(rule.trigger);
      if (!triggerControl) continue;

      const shouldBeRequired = rule.whenAnyOf.includes(
        triggerControl.value as string
      );
      for (const fieldQname of rule.requireFields) {
        this.updateConditionalField(fieldQname, shouldBeRequired);
      }
    }
  }

  /**
   * Re-apply every reactive-rule engine against the form's CURRENT values.
   *
   * Appliers run in registration order, which is `ngOnInit` order, which
   * encodes the dependency chain — `setupTypeOfSubmissionAutoSet` registers
   * before `setupMethodFieldsManagement` because the latter's composite key
   * reads `ssmt-dei:TypeOfSubmission`. Preserve that order. Mirrors role0's
   * identical method — same names, kept in sync so the two sheets stay
   * greppable together.
   *
   * Every applier writes with `emitEvent: false` (it passes `false` for
   * `isUserDriven` when invoked from the `reactiveRuleAppliers` registry
   * — see `setupLockWhenRules`'s `applyLockRule` docblock). That is safe
   * here specifically because each applier write is paired with a synchronous
   * `syncFormToState()`, so the value is already in `modifiedStateSubject`
   * before the helper returns, and the resulting state is correct regardless
   * of any debounced pipeline that fires later. The blast-radius analysis at
   * `.superpowers/sdd/2026-08-10-origin-driver-clear-cascade/emitevent-blast-radius.md`
   * §5 and §8 covers the reasoning in full.
   */
  private reapplyReactiveRules(): void {
    this.reactiveRuleAppliers.forEach((apply) => apply());
  }

  /**
   * Update conditional field validation
   */
  private updateConditionalField(
    fieldName: string,
    shouldBeRequired: boolean
  ): void {
    const control = this.filingForm.get(fieldName);
    if (control) {
      if (shouldBeRequired) {
        control.setValidators([Validators.required]);
        // Mark as touched so the empty-required error surfaces immediately
        // (mirrors role0 — without this the red error only appears after the
        // user interacts with the now-required field).
        control.markAsTouched();
      }
      else {
        control.clearValidators();
        // emitEvent: false so programmatically clearing a no-longer-required
        // field does NOT wake the autosave pipeline / markSheetAsEdited
        // subscriber (which would spuriously flip the sheet to the pencil
        // icon during a conditional cascade). Mirrors role0.
        control.setValue('', { emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });

      // CRITICAL: setValidators/clearValidators above replaces the whole
      // validator list, which would drop the cross-field date validators that
      // setupFinancialYearDateValidation() attached to the previous-year date
      // fields. Re-add them so date constraints survive a conditional change
      // (mirrors role0's reapplyDateValidatorsIfNeeded()).
      this.reapplyDateValidatorsIfNeeded(fieldName);

      // The clear branch above writes with `emitEvent: false` (deliberately —
      // see the comment there), so the cross-field subscription wired in
      // setupFinancialYearDateValidation() never wakes. Without this call the
      // SIBLING date controls keep verdicts computed against the value we just
      // wiped: switching "Disclosure of financial statements preparation for
      // current submission" from Subsequent to First time blanked both
      // previous-year dates but left `dateSequenceInvalid` on Company CURRENT
      // financial year start date — an unclearable error that held the sheet
      // at ✗ and the submit gate closed. Re-validating all four is silent
      // (`emitEvent: false`), so the autosave/pencil contract is untouched.
      this.revalidateFinancialYearDates();
    }
  }

  /**
   * Re-run validity on all four financial-year date controls.
   *
   * The three date rules (current range, previous range, previous-end <
   * current-start) are CROSS-FIELD: each is attached to more than one control,
   * so a change to any one date can only settle the others if every one of the
   * four is re-validated. This is the single place that does it — called by
   * the `valueChanges` subscription in setupFinancialYearDateValidation() (a
   * user edit), by its initial pass (mount), and by updateConditionalField()
   * (a silent conditional clear, which reaches no subscription at all).
   *
   * Always silent: `emitEvent: false` on every control, so it can never wake
   * the autosave pipeline or mark the sheet edited. Mirrors role0's method of
   * the same name — keep the two in step.
   */
  private revalidateFinancialYearDates(): void {
    for (const key of [
      FILING_INFO_FIELD_KEYS.currentYearStart,
      FILING_INFO_FIELD_KEYS.currentYearEnd,
      FILING_INFO_FIELD_KEYS.previousYearStart,
      FILING_INFO_FIELD_KEYS.previousYearEnd,
    ]) {
      this.filingForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Re-apply custom cross-field date validators to a financial-year field
   * after conditional validation has rewritten its validator list. Combines
   * the (possibly re-added) Validators.required with the stored date
   * validators so neither layer clobbers the other. Mirrors role0.
   */
  private reapplyDateValidatorsIfNeeded(fieldName: string): void {
    const control = this.filingForm.get(fieldName);
    if (!control) return;

    const customValidators = this.dateValidators[fieldName];
    if (!customValidators || customValidators.length === 0) return;

    const hasRequired = control.hasValidator(Validators.required);
    const allValidators = hasRequired
      ? [Validators.required, ...customValidators]
      : customValidators;

    control.setValidators(allValidators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Earliest selectable day on the "Company current financial year end date"
   * picker, and the copy shown when the user hovers an earlier one. Exposed as
   * fields (not getters) so the template binds a stable reference.
   */
  readonly minSupportedFinancialYearEnd = MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END;
  readonly unsupportedFinancialYearEndMessage =
    UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE;

  /**
   * `[minDate]` for a date control — the 2017-01-31 floor for the current
   * financial year END date, `undefined` (no floor) for every other date
   * field on this sheet.
   *
   * Scoped by qname on purpose: the start dates and both previous-year dates
   * legitimately precede the floor, and only the END date carries SSM's
   * constraint. See MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END.
   */
  getDateFloor(control: { qname: string }): Date | undefined {
    return control.qname === FILING_INFO_FIELD_KEYS.currentYearEnd
      ? this.minSupportedFinancialYearEnd
      : undefined;
  }

  /**
   * True when a greyed-out calendar cell is greyed out BECAUSE of the
   * unsupported-period floor, so the tooltip explains that specific reason
   * rather than appearing on any disabled day.
   *
   * `dayMeta` is PrimeNG's day descriptor: `month` is 0-based.
   */
  isUnsupportedFinancialYearEndDay(dayMeta: {
    day: number;
    month: number;
    year: number;
  }): boolean {
    return isBeforeSupportedFinancialYearEnd(
      new Date(dayMeta.year, dayMeta.month, dayMeta.day)
    );
  }

  /**
   * Setup cross-field validation for financial year date fields.
   * Validates 3 rules (shared with role0 via filing-info-common.rules.ts):
   *   1. Current Start <= Current End
   *   2. Previous Start <= Previous End
   *   3. Previous End  <  Current Start
   *
   * The shared check* primitives are wrapped as Angular ValidatorFn instances;
   * the pure role1FilingInfoValidator calls the same primitives against a flat
   * sheetData snapshot, so error messages and trigger conditions stay aligned
   * without manual sync.
   */
  private setupFinancialYearDateValidation(): void {
    const currentStartControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.currentYearStart);
    const currentEndControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.currentYearEnd);
    const previousStartControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.previousYearStart);
    const previousEndControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.previousYearEnd);

    if (!currentStartControl || !currentEndControl || !previousStartControl || !previousEndControl) {
      console.warn('One or more financial year date controls not found');
      return;
    }

    const currentRangeValidator = () => {
      const message = checkCurrentRange(currentStartControl.value, currentEndControl.value);
      return message ? { dateRangeInvalid: message } : null;
    };

    const previousRangeValidator = () => {
      const message = checkPreviousRange(previousStartControl.value, previousEndControl.value);
      return message ? { dateRangeInvalid: message } : null;
    };

    const dateSequenceValidator = () => {
      const message = checkDateSequence(previousEndControl.value, currentStartControl.value);
      return message ? { dateSequenceInvalid: message } : null;
    };

    // Floor on the CURRENT year end only. The calendar picker already greys
    // out anything before the floor via [minDate], but the field also accepts
    // a TYPED date (see the (onInput) hook on the template) which minDate does
    // not intercept — so the constraint has to exist as a validator too.
    const currentEndFloorValidator = () => {
      const message = checkCurrentEndFloor(currentEndControl.value);
      return message ? { financialYearEndUnsupported: message } : null;
    };

    // Store validators so reapplyDateValidatorsIfNeeded() can restore them
    // after a conditional-required change rewrites the validator list.
    this.dateValidators[FILING_INFO_FIELD_KEYS.currentYearStart] = [currentRangeValidator, dateSequenceValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.currentYearEnd] = [currentRangeValidator, currentEndFloorValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.previousYearStart] = [previousRangeValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.previousYearEnd] = [previousRangeValidator, dateSequenceValidator];

    currentStartControl.addValidators([currentRangeValidator, dateSequenceValidator]);
    currentEndControl.addValidators([currentRangeValidator, currentEndFloorValidator]);
    previousStartControl.addValidators([previousRangeValidator]);
    previousEndControl.addValidators([previousRangeValidator, dateSequenceValidator]);

    const markTouchedIfInvalid = (control: any) => {
      if (control.invalid) {
        control.markAsTouched({ onlySelf: true });
      }
    };

    // When any of the 4 date fields changes, re-validate all 4 (cross-field
    // dependencies) and surface errors immediately by marking invalid ones
    // touched.
    [currentStartControl, currentEndControl, previousStartControl, previousEndControl].forEach(control => {
      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.revalidateFinancialYearDates();

          markTouchedIfInvalid(currentStartControl);
          markTouchedIfInvalid(currentEndControl);
          markTouchedIfInvalid(previousStartControl);
          markTouchedIfInvalid(previousEndControl);
        });
    });

    // Run initial validation
    this.revalidateFinancialYearDates();
  }

  /**
   * Setup field disabling based on schema disabledWhen configuration.
   *
   * Every programmatic `setValue(..., { emitEvent: false })` inside this
   * method is paired with a `syncFormToState()` so the persisted state
   * mirrors the form's disabled-field truth — see the helper's docblock
   * for the parity-check rationale.
   */
  private setupFieldDisabling(): void {
    // Find all fields with disabledWhen configuration
    const fieldsWithDisabling = this.schema.controls.filter(
      (control) => control.ui.disabledWhen
    );

    fieldsWithDisabling.forEach((fieldSchema) => {
      const {
        field: triggerField,
        values: disablingValues,
        setValueTo,
        hideWhenDisabled,
      } = fieldSchema.ui.disabledWhen!;
      const targetControl = this.filingForm.get(fieldSchema.qname);
      const triggerControl = this.filingForm.get(triggerField);

      if (!targetControl || !triggerControl) return;

      // Unified apply closure — used for the initial (mount-time) state, the
      // re-applied (hydration) state, AND every subsequent valueChanges
      // emission, so all three paths share identical semantics.
      //
      // Behaviour note: the original subscribe-only path had an extra branch
      // that cleared the target to '' when `setValueTo` was undefined; the
      // mount-time path did not. Unifying on the mount-time behaviour is
      // deliberate — mirrors role0's identical unification (Task 1). No
      // role1 spec exercises a disabledWhen field lacking `setValueTo`, so
      // this does not change observable coverage.
      const applyDisableRule = (triggerValue: unknown) => {
        const shouldDisable = disablingValues.includes(triggerValue as string);

        if (shouldDisable) {
          targetControl.disable({ emitEvent: false });
          if (setValueTo !== undefined) {
            this.applyDisabledFieldValue(fieldSchema.qname, setValueTo, targetControl);
          }

          // Hide field if hideWhenDisabled flag is set
          if (hideWhenDisabled) {
            this.hiddenFields[fieldSchema.qname] = true;
          }
        }
        else {
          // A `readonly: true` control is disabled by construction in
          // `buildForm` and must STAY disabled — `disabledWhen` only ever adds a
          // second, value-driven reason to disable it, never a licence to
          // re-enable. Without this guard the else-branch re-enables
          // `DisclosureOfFinancialStatementsAuditStatus` for every
          // `StatusOfCompany` other than "Public company". Mirrors role0's
          // identical guard in its own `setupFieldDisabling` — the two engines
          // are duplicated, so a change here almost always belongs there too.
          //
          // The `readonly` guard has since been folded into
          // `isHeldDisabledByAnotherRule`, which ALSO consults an active
          // `lockWhen` on the same control: Basis is locked to MFRS by Origin,
          // and this branch used to hand it back to the user on any
          // Types-of-company change. Both sheets share that one predicate.
          if (!isHeldDisabledByAnotherRule(fieldSchema, this.filingForm, 'disabledWhen')) {
            targetControl.enable({ emitEvent: false });
          }
          // Show field when enabled
          if (hideWhenDisabled) {
            this.hiddenFields[fieldSchema.qname] = false;
          }
        }
        // Sync the post-mutation snapshot so persisted state mirrors the
        // form's disabled-field truth before any other subscriber (notably
        // the dual-path parity check) reads it.
        this.syncFormToState();
      };

      // Set initial state
      applyDisableRule(triggerControl.value);
      this.reactiveRuleAppliers.push(() => applyDisableRule(triggerControl.value));

      // Watch for changes in trigger field
      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => applyDisableRule(value));
    });
  }

  /**
   * Assign a disabled field's `setValueTo` value WITHOUT clobbering a
   * backend-hydrated value. Mirrors role0's setupFieldDisabling guard (B8
   * parity): when `setValueTo` is the empty string but the original (API)
   * state still holds a real value, keep that value instead of wiping it.
   * A non-empty `setValueTo` is always applied verbatim. Always uses
   * `{ emitEvent: false }` so it never wakes the autosave pipeline — callers
   * pair it with `syncFormToState()` as before.
   */
  private applyDisabledFieldValue(
    qname: string,
    setValueTo: string,
    targetControl: AbstractControl
  ): void {
    if (setValueTo === '') {
      const originalValue =
        this.stateService.getOriginalSheetData(SHEET_KEYS.filingInfo)[qname];
      if (originalValue && originalValue !== '') {
        // Preserve original API value instead of overwriting with empty string
        targetControl.setValue(originalValue, { emitEvent: false });
        return;
      }
    }
    // Non-empty setValueTo, or no original value to preserve: use it directly.
    targetControl.setValue(setValueTo, { emitEvent: false });
  }

  /**
   * Setup lockWhen rules from schema.
   *
   * When the trigger field equals the configured value, the target field is
   * set to `setValueTo` AND disabled (so the user cannot change it). When the
   * trigger no longer matches, the target is re-enabled.
   *
   * Used for the Basis-of-accounting field: when Origin = "Incorporated
   * outside Malaysia", the only supported value is "Malaysian Financial
   * Reporting Standards". The template renders this locked state as a
   * disabled text input (rather than a disabled dropdown) so users see it
   * as a fixed value, not a choice. Mirrors the role0 implementation —
   * keep both paths in sync.
   */
  private setupLockWhenRules(): void {
    const controlsWithLockWhen = this.schema.controls.filter(
      (control) => control.ui.lockWhen
    );

    if (controlsWithLockWhen.length === 0) {
      return;
    }

    controlsWithLockWhen.forEach((fieldSchema) => {
      const { field: triggerFieldQname, equals: triggerValue, setValueTo } = fieldSchema.ui.lockWhen!;

      const triggerControl = this.filingForm.get(triggerFieldQname);
      const targetControl = this.filingForm.get(fieldSchema.qname);

      if (!triggerControl || !targetControl) {
        console.warn(`lockWhen: Trigger control '${triggerFieldQname}' or target control '${fieldSchema.qname}' not found`);
        return;
      }

      /**
       * @param isUserDriven Distinguishes a USER-DRIVEN application (the
       *   trigger's own `valueChanges` subscriber, below) from a SYSTEM one
       *   (the initial mount-time call, and the `reactiveRuleAppliers` entry
       *   replayed by `reapplyReactiveRules()` on every hydration patch —
       *   see `setupStateListener`). Mirrors the `allowClear` idiom
       *   `setupTypeOfSubmissionAutoSet` already uses for the same
       *   system-vs-user distinction.
       *
       *   Product ruling (2026-08-10): a page load with NO user action must
       *   never raise the pencil / arm "unsaved changes" on either sheet. A
       *   SYSTEM write here uses `emitEvent: false` so it never reaches
       *   `setupStatePersistence`'s debounced pipeline (which reconciles the
       *   edited flag against `originalState$` — and a lock correction is a
       *   REAL diff from the raw, unlocked API/extracted value, so that
       *   pipeline running for a system write is exactly what used to light
       *   the pencil on a mount with Origin already at the locking value, or
       *   on a LATE `originalState$` hydration that arrives after the sheet
       *   has already finished initializing). A USER-DRIVEN write keeps
       *   `emitEvent: true` so `setupTypeOfSubmissionAutoSet`'s
       *   `accountingStandardsControl.valueChanges` subscriber still derives
       *   Type of submission the same way it always did.
       *
       *   This is SAFE on role1 (unlike a naive port of role0's mechanism)
       *   because `syncFormToState()` below (which role0's `applyLockRule`
       *   does NOT have) pushes the same value into `modifiedStateSubject`
       *   SYNCHRONOUSLY and UNCONDITIONALLY — regardless of `isUserDriven` —
       *   with `skipEditedMark: true`, so state never goes stale on the
       *   silent SYSTEM path and the pencil is never armed by it either. See
       *   the blast-radius analysis at
       *   .superpowers/sdd/2026-08-10-origin-driver-clear-cascade/emitevent-blast-radius.md
       *   §1 and §8 for the full reasoning, and the "FIX 1" gate specs
       *   (`role1-fix1-hydration-no-pencil.spec.ts`) for the regression
       *   coverage on both the mount and the late-hydration path.
       *
       *   `setupTypeOfSubmissionAutoSet`'s own applier / mount-time call
       *   re-reads Basis's CURRENT value directly (no emission needed) — see
       *   that method's docblock — so a SYSTEM lock application still derives
       *   Type of submission correctly even though it emits nothing.
       */
      const applyLockRule = (triggerFieldValue: string | null, isUserDriven: boolean) => {
        if (triggerFieldValue === triggerValue) {
          targetControl.setValue(setValueTo, { emitEvent: isUserDriven });
          targetControl.disable({ emitEvent: false });
          // No direct `deriveTypeOfSubmissionFromBasis` call here (deleted
          // 2026-08-10): a USER-DRIVEN write emits, so
          // `setupTypeOfSubmissionAutoSet`'s own
          // `accountingStandardsControl.valueChanges` subscriber derives Type
          // of submission the same way a user-driven Basis change already
          // did. A SYSTEM write emits nothing, but that method's OWN mount /
          // reapply call independently re-reads Basis's just-locked value —
          // see its docblock. `ngOnInit` keeps `setupLockWhenRules` (this
          // method) registered BEFORE `setupTypeOfSubmissionAutoSet` because
          // this order IS LOAD-BEARING. Reversing it leaves Type of submission
          // stuck at empty instead of deriving the correct FS-MFRS/FS-MPERS —
          // see the regression guard in `role1-driver-clear-cascade.spec.ts`'s
          // "ngOnInit order" describe, which pins both directions and explains
          // why this order must be preserved rather than treated as
          // interchangeable.
          // `setupLockWhenReassert`'s OWN write ALSO emits (Task 7,
          // 2026-08-10 — unified with role0), so it derives Type of
          // submission the same way, via the same subscriber — see that
          // method's own docblock.
        } else if (
          targetControl.disabled &&
          !isHeldDisabledByAnotherRule(fieldSchema, this.filingForm, 'lockWhen')
        ) {
          // 2026-08-14: guarded against re-enabling a control that a
          // `disabledWhen` rule still holds disabled. Lifting the lock (Origin
          // leaving "Incorporated outside Malaysia") must not override Types of
          // company = "Company limited by guarantee", which pins Basis to MFRS
          // in its own right. Mirrors role0's identical guard.
          targetControl.enable({ emitEvent: false });
        }
        this.syncFormToState();
      };

      applyLockRule(triggerControl.value, false); // SYSTEM: initial mount.
      this.reactiveRuleAppliers.push(() => applyLockRule(triggerControl.value, false)); // SYSTEM: hydration re-apply.

      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          applyLockRule(value, true); // USER-DRIVEN.
        });
    });
  }

  /**
   * Product ruling (2026-08-10): RE-LOCK IMMEDIATELY. `setupDriverClearCascade`
   * clears a `lockWhen` target (e.g. Basis) when an UPSTREAM driver (Types of
   * company) is cleared, and `setupFieldDisabling`'s own `disabledWhen`
   * else-branch then re-enables it because ITS OWN trigger no longer matches
   * — `setupLockWhenRules`'s `applyLockRule` never sees this, because it only
   * watches the Origin trigger, and Origin never changed. Left alone, that
   * leaves an editable, empty Basis dropdown under a foreign Origin the lock
   * exists to forbid entirely (the user could pick MPERS, which neither the
   * editor nor the headless validator would reject — see
   * docs/specifications/sheets/01_filing-info.md Field #16). So: watch the
   * TARGET's own value, and the instant it goes empty while the trigger is
   * STILL at the locking value, write `setValueTo` straight back and
   * re-disable.
   *
   * Task 7 (2026-08-10): unified with role0's implementation — writes with
   * `emitEvent: true` and has NO direct `deriveTypeOfSubmissionFromBasis`
   * call. The re-assert fires in response to a USER action (a lockWhen
   * target being cleared), so — unlike the SYSTEM mount/hydration paths in
   * `setupLockWhenRules`'s `applyLockRule`, which stay silent on purpose —
   * emitting here is correct: it wakes `setupTypeOfSubmissionAutoSet`'s own
   * `accountingStandardsControl.valueChanges` subscriber the same way any
   * other user-driven Basis change already does, so a hardcoded
   * `fieldSchema.qname === '…Basis…'` gate on a direct derive call is
   * unnecessary — see `role1-driver-clear-cascade.spec.ts`'s "not
   * qname-gated" spec, which pins this by aliasing the schema entry's qname
   * away from the literal Basis string and asserting the derivation still
   * happens. `syncFormToState()` still runs unconditionally, same as before.
   *
   * This must NOT fire when the trigger itself is what changed (the Origin-
   * clear path): `applyLockRule` in `setupLockWhenRules` already re-enables +
   * leaves the target cleared in that case, and re-reading
   * `triggerControl.value` here (rather than trusting subscription order) is
   * what tells the two paths apart — by the time this fires, Origin has
   * already been updated to whatever the user set it to.
   *
   * DELIBERATELY A SEPARATE METHOD FROM `setupLockWhenRules`, called LATER in
   * `ngOnInit` (after `setupTypeOfSubmissionAutoSet`) — NOT folded into the
   * same forEach pass, and the registration order below `setupTypeOfSubmissionAutoSet`
   * is still LOAD-BEARING even though this write now emits. `setupTypeOfSubmissionAutoSet`
   * also subscribes to Basis's OWN `valueChanges`, on the SAME control this
   * re-assert watches. RxJS's `EventEmitter`/`Subject` delivers one `next()`
   * call to every subscriber IN SUBSCRIPTION ORDER using the SAME emitted
   * value, even though this re-assert's own nested `setValue()` triggers a
   * re-emission first — that nested emission runs to completion, but the
   * OUTER emission then resumes and still hands its *original, now-stale*
   * value to any remaining subscriber. If this re-assert subscribed to Basis
   * BEFORE `setupTypeOfSubmissionAutoSet` (as it would from inside
   * `setupLockWhenRules`, which runs earlier), the cascade's `''` emission
   * would re-lock Basis first (nested emission derives `FS-MFRS`), and THEN
   * the outer `''` would still reach `setupTypeOfSubmissionAutoSet`'s
   * subscriber, which would clear Type of submission a second time — landing
   * on `''` instead of `FS-MFRS`. Measured with
   * `role1-lockwhen-cleared-origin.spec.ts` during development; registering
   * this subscription AFTER `setupTypeOfSubmissionAutoSet`'s makes THIS
   * subscriber the last to see the outer emission, so its re-lock (and the
   * nested `FS-MFRS` derivation it triggers) is the final word. Do not merge
   * this back into `setupLockWhenRules` without re-verifying that ordering.
   */
  private setupLockWhenReassert(): void {
    const controlsWithLockWhen = this.schema.controls.filter(
      (control) => control.ui.lockWhen
    );

    controlsWithLockWhen.forEach((fieldSchema) => {
      const { field: triggerFieldQname, equals: triggerValue, setValueTo } = fieldSchema.ui.lockWhen!;

      const triggerControl = this.filingForm.get(triggerFieldQname);
      const targetControl = this.filingForm.get(fieldSchema.qname);

      if (!triggerControl || !targetControl) {
        return;
      }

      targetControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (this.isLockWhenReasserting) {
            return;
          }
          if (!isClearedValue(value) || triggerControl.value !== triggerValue) {
            return;
          }
          this.isLockWhenReasserting = true;
          try {
            targetControl.setValue(setValueTo, { emitEvent: true });
            targetControl.disable({ emitEvent: false });
            this.syncFormToState();
          } finally {
            this.isLockWhenReasserting = false;
          }
        });
    });
  }

  /**
   * Template helper: returns true when a field's lockWhen rule is currently
   * active (i.e. the trigger field's current value matches). Drives the
   * disabled-text-input rendering branch in the template.
   */
  isLockActive(control: FormControlSchema): boolean {
    const lock = control.ui.lockWhen;
    if (!lock) return false;
    const trigger = this.filingForm?.get(lock.field);
    if (!trigger || trigger.value !== lock.equals) return false;
    // The trigger matching is necessary but not sufficient: the target
    // control could in principle be re-enabled by another rule while the
    // trigger still matches (e.g. `setupFieldDisabling`'s else-branch, mid-
    // cascade, before the re-assert subscriber below has run). Since
    // 2026-08-10 that window closes synchronously — the same emission that
    // clears the target re-locks it before this getter is ever read from
    // the template — but the disabled check stays as a defensive mirror of
    // the control's REAL state rather than trusting the trigger alone.
    const targetControl = this.filingForm?.get(control.qname);
    return !!targetControl && targetControl.disabled;
  }

  /**
   * Template helper: returns the locked value for a field whose lockWhen
   * rule is currently active. Returns empty string if not locked.
   */
  getLockedValue(control: FormControlSchema): string {
    return control.ui.lockWhen?.setValueTo ?? '';
  }

  /**
   * Setup conditional dropdown options based on schema optionsFilteredBy configuration
   * Supports both single-field and multi-field filtering
   */
  private setupConditionalOptions(): void {
    // Find all fields with optionsFilteredBy configuration
    const fieldsWithFiltering = this.schema.controls.filter(
      (control) => control.ui.optionsFilteredBy
    );

    fieldsWithFiltering.forEach((fieldSchema) => {
      const filterConfig = fieldSchema.ui.optionsFilteredBy!;
      const {
        field: singleField,
        fields: multipleFields,
        mappingSource,
      } = filterConfig;

      // Determine if single-field or multi-field filtering
      const triggerFields =
        multipleFields || (singleField ? [singleField] : []);
      const isMultiField = !!multipleFields;

      if (triggerFields.length === 0) {
        console.error(
          'No trigger fields specified for conditional filtering:',
          fieldSchema.qname
        );
        return;
      }

      // Resolve mappings from typeDict or use inline mappings (backward compatibility)
      let mappings: { [key: string]: string[] };

      if (mappingSource) {
        // Load from typeDict conditionalMappings
        const mappingKey = mappingSource.replace('conditional:', '');
        const conditionalMapping =
          this.typeDict.conditionalMappings?.[mappingKey];

        if (!conditionalMapping || !conditionalMapping.mappings) {
          console.error(
            `Conditional mapping '${mappingKey}' not found in types.dict.json`
          );
          return;
        }

        mappings = conditionalMapping.mappings;
      }
      else if (filterConfig.mappings) {
        // Inline mappings (backward compatibility)
        mappings = filterConfig.mappings;
      }
      else {
        console.error(
          'No mappings configuration found for conditional field:',
          fieldSchema.qname
        );
        return;
      }

      const targetControl = this.filingForm.get(fieldSchema.qname);
      if (!targetControl) {
        console.warn(`Target control not found: ${fieldSchema.qname}`);
        return;
      }

      // Get all trigger controls
      const triggerControls = triggerFields
        .map((fieldName) => this.filingForm.get(fieldName))
        .filter((ctrl) => ctrl !== null);

      if (triggerControls.length === 0) {
        console.warn(
          `No valid trigger controls found for: ${fieldSchema.qname}`
        );
        return;
      }

      // Helper function to build composite key from multiple field values
      const buildCompositeKey = (): string => {
        if (isMultiField) {
          // Multi-field: Join values with pipe separator (e.g., "Public company|Malaysian Financial Reporting Standards")
          const values = triggerFields.map((fieldName) => {
            const control = this.filingForm.get(fieldName);
            return control?.value || '';
          });
          return values.join('|');
        }
        else {
          // Single-field: Use value directly
          return triggerControls[0]!.value || '';
        }
      };

      // De-duplicated union of every value across every mapping entry for
      // this control, preserving first-appearance order. Used as the
      // cleared-driver fallback below instead of the raw, unfiltered enum:
      // the raw enum can contain values (e.g. "Company limited by
      // guarantee" in TypeOfCompanyByOrigin) that are not reachable under
      // ANY supported driver value, and offering them lets the user select
      // a value whose own `disabledWhen` side-effects (e.g. locking Basis
      // to MFRS) were never meant to be user-triggerable. Falls back to
      // `undefined` (raw-enum behaviour) if the mapping cannot be resolved.
      const mappedUnionOptions: string[] | undefined = (() => {
        if (!mappings) return undefined;
        const seen = new Set<string>();
        const union: string[] = [];
        for (const values of Object.values(mappings)) {
          if (!Array.isArray(values)) continue;
          for (const value of values) {
            if (!seen.has(value)) {
              seen.add(value);
              union.push(value);
            }
          }
        }
        return union.length > 0 ? union : undefined;
      })();

      // Helper function to update filtered options based on composite key
      const updateFilteredOptions = (fromHydration = false) => {
        const compositeKey = buildCompositeKey();
        const allowedOptions = mappings[compositeKey];

        if (allowedOptions) {
          // Re-arm: a driver re-selected after an empty-driver hydration pass
          // must undo the disable + validator strip below. `mapSchemaValidators`
          // deliberately never adds `Validators.required` (see
          // schema-validators.util.ts), so it must be re-added here from the
          // same `isUnconditionallyRequired` source buildForm() uses — otherwise
          // a mandatory field re-arms with ZERO validators. The `disabledWhen`
          // exclusion prevents this re-arm from fighting setupFieldDisabling's
          // own applier, which registers earlier in the same re-apply pass.
          if (
            targetControl.disabled &&
            !fieldSchema.ui.readonly &&
            !fieldSchema.ui.disabledWhen
          ) {
            targetControl.enable({ emitEvent: false });
            const rearmed = [...mapSchemaValidators(fieldSchema as any)];
            if (isUnconditionallyRequired(fieldSchema as any)) {
              rearmed.unshift(Validators.required);
            }
            targetControl.setValidators(rearmed);
            targetControl.updateValueAndValidity({ emitEvent: false });
          }

          // Filter the dropdown options based on allowed values
          this.filteredDropdownOptions[fieldSchema.qname] = allowedOptions.map(
            (value) => ({
              label: value,
              value: value,
            })
          );

          // Clear the field value if it's not in the allowed options
          const currentValue = targetControl.value;
          if (currentValue && !allowedOptions.includes(currentValue)) {
            targetControl.setValue('', { emitEvent: false });
            // Sync the cleared value into modifiedStateSubject. Without
            // this, the persisted state retains the now-invalid prior
            // value while the form shows empty — the pure validator's
            // pattern check would fire on the stale value.
            this.syncFormToState();
          }
        }
        else if (fromHydration && !buildCompositeKey()) {
          // Extracted NULL driver: the legal domain is unknowable, so the
          // dependent renders empty + disabled + unvalidated and the mandatory
          // error surfaces on the DRIVER instead.
          //
          // Still gated on `fromHydration`, but no longer because the user
          // cascade is blocked — it shipped 2026-08-10. The two paths differ
          // deliberately: a USER clear leaves the dependent ENABLED and
          // required so they can re-pick immediately (setupDriverClearCascade),
          // whereas an extraction NULL disables it because no user action
          // implied a domain. This branch must still never fire from the
          // valueChanges subscriber below.
          //
          // EXCEPT for a `readonly` control, which is never user-pickable, so
          // "let the user re-pick once the driver is known" has no meaning for
          // it: wiping one strips a schema `defaultValue` nothing will ever
          // re-seat, leaving the field permanently on "Please select". Mirrors
          // role0's identical branch — read the fuller rationale there.
          //
          // Currently INERT on role1: its only `optionsFilteredBy` control is
          // `ssmt-dei:TypeOfCompany`, which is not readonly. Kept identical to
          // role0 anyway; the pair has a documented history of diverging.
          if (fieldSchema.ui.readonly) {
            if (!targetControl.value && fieldSchema.ui.defaultValue) {
              targetControl.setValue(fieldSchema.ui.defaultValue, {
                emitEvent: false,
              });
            }
          } else {
            targetControl.setValue('', { emitEvent: false });
          }
          targetControl.disable({ emitEvent: false });
          targetControl.clearValidators();
          targetControl.updateValueAndValidity({ emitEvent: false });
          // A preserved readonly value needs its option to still exist —
          // `<p-select>` renders its placeholder, not the value, when the
          // value matches no option. See role0's identical branch.
          this.filteredDropdownOptions[fieldSchema.qname] =
            fieldSchema.ui.readonly && mappedUnionOptions
              ? mappedUnionOptions.map((value) => ({ label: value, value }))
              : [];
          // Sync the cleared value into modifiedStateSubject — sibling
          // appliers (applyLockRule, method-fields, the allowedOptions clear
          // above) all do this; without it the pure validator (which reads
          // modifiedStateSubject) would keep seeing the stale extracted value
          // until an unrelated edit.
          this.syncFormToState();
        }
        else {
          // Cleared driver (or a driver value with no explicit mapping
          // entry): offer the union of every value that is reachable under
          // ANY supported driver value, not the raw unfiltered enum — see
          // `mappedUnionOptions` above for why. Fall back to the raw enum
          // only if the mapping itself could not be resolved into a union.
          if (mappedUnionOptions) {
            this.filteredDropdownOptions[fieldSchema.qname] =
              mappedUnionOptions.map((value) => ({ label: value, value }));
          } else {
            const enumKey =
              fieldSchema.ui.enumSource?.replace('type:', '') || '';
            this.filteredDropdownOptions[fieldSchema.qname] =
              this.dropdownOptions[enumKey] || [];
          }
        }
      };

      // Set initial filtered options
      updateFilteredOptions();
      this.reactiveRuleAppliers.push(() => updateFilteredOptions(true));

      // Watch for changes in ANY trigger field
      triggerControls.forEach((triggerControl, index) => {
        triggerControl!.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            updateFilteredOptions();
          });
      });
    });
  }

  /**
   * Setup Bank Negara Malaysia field auto-management based on Type of Submission
   */
  private setupBankNegaraFieldManagement(): void {
    // Find fields with autoManageByTypeOfSubmission flag
    const fieldsToAutoManage = this.schema.controls.filter(
      (control) => control.ui.autoManageByTypeOfSubmission
    );

    if (fieldsToAutoManage.length === 0) return;

    const typeOfSubmissionControl = this.filingForm.get(
      'ssmt-dei:TypeOfSubmission'
    );
    if (!typeOfSubmissionControl) {
      console.warn(
        'Type of Submission control not found for Bank Negara auto-management'
      );
      return;
    }

    fieldsToAutoManage.forEach((fieldSchema) => {
      const targetControl = this.filingForm.get(fieldSchema.qname);
      if (!targetControl) return;

      // Helper function to update visibility and value
      const updateFieldBasedOnTypeOfSubmission = (
        typeOfSubmissionValue: string | null
      ) => {
        if (!typeOfSubmissionValue || typeOfSubmissionValue === '') {
          // Hide field when Type of Submission is empty.
          //
          // Mirrors role0's `setupBankNegaraFieldManagement` (see the full
          // rationale there): a hidden control must also be DISABLED, because
          // an enabled mandatory control still counts towards
          // `filingForm.valid` — the Submit gate — and greys Submit out with
          // no error visible anywhere.
          //
          // On role1 this engine is currently INERT: the only two qnames
          // carrying `autoManageByTypeOfSubmission` live in role-1.form.json's
          // `_archived.delegatedToRole2` list, not in its `controls`, so
          // `fieldsToAutoManage` is empty and this callback never runs. It is
          // fixed anyway to keep the two duplicated engines identical — the
          // role0/role1 pair has a documented history of diverging silently.
          this.hiddenFields[fieldSchema.qname] = true;
          targetControl.disable({ emitEvent: false });
        }
        else {
          // Show field, disable it, and set value based on Type of Submission
          this.hiddenFields[fieldSchema.qname] = false;
          targetControl.disable({ emitEvent: false }); // Disable to prevent user modification

          if (typeOfSubmissionValue === 'FS-BNM') {
            targetControl.setValue(
              'Company regulated by Bank Negara Malaysia',
              { emitEvent: false }
            );
          }
          else {
            targetControl.setValue(
              'Company not regulated by Bank Negara Malaysia',
              { emitEvent: false }
            );
          }
        }
        // Sync the auto-managed Bank Negara value into persisted state so
        // the pure validator (which reads modifiedStateSubject, not the
        // form) sees the same field value the user is shown.
        this.syncFormToState();
      };

      // Set initial state
      updateFieldBasedOnTypeOfSubmission(typeOfSubmissionControl.value);
      this.reactiveRuleAppliers.push(() =>
        updateFieldBasedOnTypeOfSubmission(typeOfSubmissionControl.value)
      );

      // Watch for changes in Type of Submission
      typeOfSubmissionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          updateFieldBasedOnTypeOfSubmission(value);
        });
    });
  }

  /**
   * Setup automatic Type of Submission based on Accounting Standards.
   * MFRS → FS-MFRS, MPERS → FS-MPERS.
   *
   * Ported from role0's `setupTypeOfSubmissionAutoSet` — role1 owns the same
   * derived pair (both keys are in role-1.form.json) and
   * `docs/specifications/sheets/01_filing-info.md` Field #18 records the value
   * as "seeded from the basis-of-accounting-standards selection by component
   * logic", but role1 shipped without that logic. Until now the derivation ran
   * ONLY in the state layer (`FilingFormStateService.deriveTypeOfSubmission`),
   * which writes `modifiedStateSubject` — and role1 listens to `originalState$`
   * (API loads) only, so nothing patched the derived value back into the form.
   * The displayed dropdown stayed frozen on the loaded value until the sheet
   * was remounted and `loadSavedState` re-patched it from state.
   *
   * `allowClear` mirrors role0: the INITIAL call may never clear, or it would
   * wipe an API/persisted submission type on every filing whose Basis failed to
   * round-trip (and clobber a value owned by another flow — KFI-*, FS-EPC). Only
   * a genuine TRANSITION to an empty Basis — the dropdown's [showClear] X, or the
   * Delete key — may clear.
   *
   * The `defaultValue: "FS-MFRS"` this guard used to protect on
   * `ssmt-dei:TypeOfSubmission` was REMOVED from role-1.form.json (2026-07-30):
   * with the backend now returning `null` for both Basis and TypeOfSubmission it
   * presented an unearned MFRS. See the fuller note on role0's
   * `setupTypeOfSubmissionAutoSet`. Do NOT reinstate it.
   */
  private setupTypeOfSubmissionAutoSet(): void {
    const accountingStandardsControl = this.filingForm.get(
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
    );
    const typeOfSubmissionControl = this.filingForm.get('ssmt-dei:TypeOfSubmission');

    if (!accountingStandardsControl || !typeOfSubmissionControl) {
      console.warn('Accounting Standards or Type of Submission control not found');
      return;
    }

    // Set initial value — never clears (see allowClear below).
    this.deriveTypeOfSubmissionFromBasis(accountingStandardsControl.value, false);
    // allowClear:false — a hydration pass is NOT a user transition. Clearing
    // here would wipe the seeded FS-MFRS default on every extracted filing
    // whose Basis failed to map.
    this.reactiveRuleAppliers.push(() =>
      this.deriveTypeOfSubmissionFromBasis(accountingStandardsControl.value, false)
    );

    // Watch for changes in Accounting Standards — a transition MAY clear.
    accountingStandardsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.deriveTypeOfSubmissionFromBasis(value, true);
      });
  }

  /**
   * Derives `ssmt-dei:TypeOfSubmission` from a Basis-of-accounting-standards
   * value: MFRS -> FS-MFRS, MPERS -> FS-MPERS, cleared (+ allowClear) -> ''.
   *
   * Called only from `setupTypeOfSubmissionAutoSet` (mount, hydration
   * re-apply, and the `accountingStandardsControl.valueChanges` subscriber)
   * — as of Task 7 (2026-08-10) neither `setupLockWhenRules`'s
   * `applyLockRule` nor `setupLockWhenReassert` calls this directly. Both now
   * write Basis with `emitEvent: true` on their user-driven paths, which
   * reaches the `valueChanges` subscriber above through the ordinary seam a
   * user-driven Basis change always used — see
   * `.superpowers/sdd/2026-08-10-origin-driver-clear-cascade/emitevent-blast-radius.md`
   * §6 (for `applyLockRule`) and `setupLockWhenReassert`'s own docblock (for
   * the re-assert) for why deleting the direct calls there is safe, and for
   * the registration-order dependency this creates.
   */
  private deriveTypeOfSubmissionFromBasis(accountingStandards: string | null, allowClear: boolean): void {
    const typeOfSubmissionControl = this.filingForm.get('ssmt-dei:TypeOfSubmission');
    if (!typeOfSubmissionControl) {
      return;
    }

    if (accountingStandards === 'Malaysian Financial Reporting Standards') {
      typeOfSubmissionControl.setValue('FS-MFRS', { emitEvent: false });
    } else if (accountingStandards === 'Malaysian Private Entities Reporting Standards') {
      typeOfSubmissionControl.setValue('FS-MPERS', { emitEvent: false });
    } else if (!accountingStandards && allowClear) {
      // Basis was CLEARED. The derived half must go with it: TypeOfSubmission
      // is `readonly: true` in role-1.form.json, hence built disabled with no
      // clear icon of its own, so a latched stale value here is uncorrectable
      // by the user while sheet applicability and the MFRS/MPERS schema switch
      // keep reading it.
      //
      // Only the two values THIS rule derives are cleared: a submission type
      // that came from another flow (KFI-*, FS-EPC) is never clobbered.
      const current = typeOfSubmissionControl.value;
      if (current === 'FS-MFRS' || current === 'FS-MPERS') {
        typeOfSubmissionControl.setValue('', { emitEvent: false });
      }
    }
    // Any other Basis value (e.g. "Others") leaves TypeOfSubmission untouched.
  }

  /**
   * [Not supported in role1] Setup KFI-MFRS/KFI-MPERS exemption field
   * auto-management.
   *
   * When Type of Submission is "KFI-MFRS" or "KFI-MPERS", the exemption
   * field must be set to "Yes". role1 ships FS-MFRS / FS-MPERS only, and
   * the targeted exemption field is delegated to role2 (not in role1's
   * 27-field schema). Method body commented out as reference; re-enable
   * the caller in ngOnInit (and uncomment this body) only if KFI variants
   * are ever adopted for role1 AND the exemption field moves back here.
   */
  // private setupKFIMFRSExemptionFieldManagement(): void {
  //   const typeOfSubmissionControl = this.filingForm.get(
  //     'ssmt-dei:TypeOfSubmission'
  //   );
  //   const exemptionFieldControl = this.filingForm.get(
  //     'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister'
  //   );
  //
  //   if (!typeOfSubmissionControl || !exemptionFieldControl) {
  //     console.warn('Type of Submission or Exemption field control not found');
  //     return;
  //   }
  //
  //   // Helper function to update exemption field based on Type of Submission
  //   const updateExemptionField = (typeOfSubmissionValue: string | null) => {
  //     if (
  //       typeOfSubmissionValue === 'KFI-MFRS' ||
  //       typeOfSubmissionValue === 'KFI-MPERS'
  //     ) {
  //       // Set to "Yes" and disable the field
  //       exemptionFieldControl.setValue('Yes', { emitEvent: false });
  //       exemptionFieldControl.disable();
  //     }
  //     else {
  //       // Enable the field for user input
  //       exemptionFieldControl.enable();
  //     }
  //   };
  //
  //   // Set initial state
  //   updateExemptionField(typeOfSubmissionControl.value);
  //
  //   // Watch for changes in Type of Submission
  //   typeOfSubmissionControl.valueChanges
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((value) => {
  //       updateExemptionField(value);
  //     });
  // }

  /**
   * Setup method fields management based on 4-field context
   * Fields: Status of Company, Type of Company, Accounting Standards, Type of Submission
   */
  private setupMethodFieldsManagement(): void {
    // Find fields with autoManageByContext flag
    const fieldsToAutoManage = this.schema.controls.filter(
      (control) => control.ui.autoManageByContext
    );

    if (fieldsToAutoManage.length === 0) return;

    // All 5 method fields
    const allMethodFields = [
      'ssmt:MethodUsedForPreparingStatementOfFinancialPosition',
      'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss',
      'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome',
      'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity',
      'ssmt:MethodUsedForPreparingStatementOfCashFlows',
    ];

    // Get all 4 trigger controls
    const statusControl = this.filingForm.get('ssmt-dei:StatusOfCompany');
    const typeControl = this.filingForm.get('ssmt-dei:TypeOfCompany');
    const standardsControl = this.filingForm.get(
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
    );
    const submissionControl = this.filingForm.get('ssmt-dei:TypeOfSubmission');

    if (
      !statusControl ||
      !typeControl ||
      !standardsControl ||
      !submissionControl
    ) {
      console.warn(
        'One or more trigger controls not found for method fields management'
      );
      return;
    }

    // Load the conditional mapping from typeDict
    const conditionalMapping =
      this.typeDict.conditionalMappings?.['MethodFieldsEnabledByContext'];
    if (!conditionalMapping || !conditionalMapping.mappings) {
      console.error(
        'Conditional mapping "MethodFieldsEnabledByContext" not found in types.dict.json'
      );
      return;
    }

    const mappings = conditionalMapping.mappings;

    // Helper function to build composite key from 4 field values
    const buildCompositeKey = (): string => {
      const status = statusControl.value || '';
      const type = typeControl.value || '';
      const standards = standardsControl.value || '';
      const submission = submissionControl.value || '';

      // Extract prefix from Type of Submission (e.g., "FS-MFRS" → "FS", "KFI-MPERS" → "KFI")
      const submissionPrefix = submission.split('-')[0] || '';

      // Build composite key: "Public company|Company limited by shares|Malaysian Financial Reporting Standards|KFI"
      return `${status}|${type}|${standards}|${submissionPrefix}`;
    };

    /**
     * True when all four trigger controls carry a value, i.e. the composite key
     * can actually be matched against the mapping table.
     *
     * A cleared driver (the dropdowns' new [showClear] X) contributes an empty
     * segment, so the key misses EVERY mapping and every method field lands in
     * the "hide, disable, clear value" branch below — and on this sheet the
     * blanked values reach persisted state immediately via syncFormToState(),
     * where applyDefaultValuesToState reseats schema defaults. Re-selecting the
     * driver therefore restores defaults, not the user's choices. "Key incomplete
     * because a driver is empty" is NOT "key valid but this field is
     * inapplicable"; only the latter clears.
     */
    const hasCompleteContext = (): boolean =>
      !!statusControl.value &&
      !!typeControl.value &&
      !!standardsControl.value &&
      !!submissionControl.value;

    // Helper function to update method fields visibility based on composite key
    const updateMethodFieldsVisibility = (isInitialLoad = false) => {
      // Special handling for FS-EPC: always disable all method fields regardless of accounting standard
      const submissionType = submissionControl.value || '';
      if (submissionType === 'FS-EPC') {
        // Disable all method fields
        allMethodFields.forEach((fieldQname) => {
          const fieldControl = this.filingForm.get(fieldQname);
          if (!fieldControl) return;

          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue('', { emitEvent: false });
        });
        // Sync the FS-EPC blackout into persisted state. The cleared values
        // must reach modifiedStateSubject so the pure validator does not
        // see a stale prior selection that would no longer be valid under
        // FS-EPC's "all method fields blacked out" rule.
        this.syncFormToState();
        return; // Exit early
      }

      // A driver is empty, so the composite key can match no mapping.
      if (!hasCompleteContext()) {
        if (!isInitialLoad) {
          // Mid-session clear: leave values, visibility and persisted state
          // exactly as they are until the user re-selects the driver.
          return;
        }
        // INITIAL LOAD with an incomplete context. Reached on a brand-new
        // filing (nothing to lose) and — the case this guard exists for — on a
        // REMOUNT after the user cleared a driver: loadSavedState() has
        // already patched the saved incomplete context plus the user's method
        // selections into the form before setupMethodFieldsManagement() runs.
        // Falling through would blank all five fields AND syncFormToState()
        // would persist the wipe, i.e. the mid-session data loss deferred by a
        // single navigation.
        //
        // Visibility/enablement still follow the historical "hide until a
        // context is picked" rendering; only the destructive setValue('') and
        // the state write are skipped.
        allMethodFields.forEach((fieldQname) => {
          const fieldControl = this.filingForm.get(fieldQname);
          if (!fieldControl) return;
          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
        });
        return;
      }

      // Normal composite key logic
      const compositeKey = buildCompositeKey();
      const mappingValue = mappings[compositeKey];

      // Parse mapping value (can be array or object)
      let enabledFields: string[] = [];
      let disabledWithDefaults: { [key: string]: string } = {};

      if (Array.isArray(mappingValue)) {
        // Simple array structure: all fields are enabled
        enabledFields = mappingValue;
      }
      else if (mappingValue && typeof mappingValue === 'object') {
        // Object structure: enabled + disabledWithDefaults
        enabledFields = mappingValue.enabled || [];
        disabledWithDefaults = mappingValue.disabledWithDefaults || {};
      }

      // Update each method field
      allMethodFields.forEach((fieldQname) => {
        const fieldControl = this.filingForm.get(fieldQname);
        if (!fieldControl) return;

        const isEnabled = enabledFields.includes(fieldQname);
        const hasDisabledDefault =
          disabledWithDefaults.hasOwnProperty(fieldQname);

        if (isEnabled) {
          // Show and enable the field
          this.hiddenFields[fieldQname] = false;
          fieldControl.enable({ emitEvent: false });
        }
        else if (hasDisabledDefault) {
          // Show but disable the field, set default value
          this.hiddenFields[fieldQname] = false;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue(disabledWithDefaults[fieldQname], {
            emitEvent: false,
          });
        }
        else {
          // Hide and disable the field, clear value
          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue('', { emitEvent: false });
        }
      });
      // Sync the cumulative method-field mutations after the forEach so
      // modifiedStateSubject reflects every disabled-default and cleared
      // value the user is shown. Single sync per visibility update keeps
      // BehaviorSubject churn low while still closing the parity gap.
      this.syncFormToState();
    };

    // Set initial state
    updateMethodFieldsVisibility(true);
    // isInitialLoad:true — applies the "hide until a context is picked"
    // rendering when the composite key is incomplete. `false` returns early and
    // would leave the five method fields visible and enabled under a context
    // that matches no mapping. Both branches preserve VALUES (the destructive
    // setValue('') lives in the complete-context-but-excluded branch, which
    // neither reaches), so this is a rendering choice, not a data-safety one.
    // Mirrors role0's identical registration.
    this.reactiveRuleAppliers.push(() => updateMethodFieldsVisibility(true));

    // Watch for changes in ANY of the 4 trigger fields
    [statusControl, typeControl, standardsControl, submissionControl].forEach(
      (control) => {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          updateMethodFieldsVisibility();
        });
      }
    );
  }

  /**
   * Auto-fill demo data
   */
  // autoFillDemo(): void {
  //   const currentYear = new Date().getFullYear();
  //   const previousYear = currentYear - 1;
  //   const twYearsAgo = currentYear - 2;

  //   const demoData = {
  //     'ssmt-dei:NewCompanyRegistrationNumber': '202001012345',
  //     'ssmt-dei:CompanyRegistrationNumber': '202001012345',
  //     'ssmt-dei:NameOfReportingEntity': 'ABC Manufacturing Sdn Bhd',
  //     'ssmt-dei:FormerNameOfCompany': '',
  //     'ssmt-dei:OriginOfCompany': 'Incorporated in Malaysia',
  //     'ssmt-dei:StatusOfCompany': 'Private company',
  //     'ssmt-dei:TypeOfCompany': 'Company limited by shares',
  //     'ssmt-dei:ApplicationOfSubmission': 'Ordinary filing',
  //     'ssmt-dei:StatutoryDeclarationForRectification': '',
  //     'ssmt-dei:CourtOrderReferenceNumber': '',
  //     'ssmt-dei:DisclosureOfFinancialStatementsPreparationForCurrentSubmission':
  //       'Subsequent preparation of financial statements',
  //     'ssmt-dei:CompanyCurrentFinancialYearStartDate': new Date(
  //       previousYear,
  //       0,
  //       1
  //     ),
  //     'ssmt-dei:CompanyCurrentFinancialYearEndDate': new Date(
  //       previousYear,
  //       11,
  //       31
  //     ),
  //     'ssmt-dei:CompanyPreviousFinancialYearStartDate': new Date(
  //       twYearsAgo,
  //       0,
  //       1
  //     ),
  //     'ssmt-dei:CompanyPreviousFinancialYearEndDate': new Date(
  //       twYearsAgo,
  //       11,
  //       31
  //     ),
  //     'ssmt-dei:StatusOfCarryingOnBusinessDuringFinancialYear': 'Active',
  //     'ssmt-dei:DisclosureOfFinancialStatementsAuditStatus': 'Audited',
  //     'ssmt-dei:AuditExemptionCategory': '',
  //     'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements':
  //       'MPERS',
  //     'ssmt-dei:DisclosureOfOtherAccountingStandardsApplied': '',
  //     'ssmt-dei:TypeOfSubmission': 'FS-MPERS',
  //     'ssmt-dei-ee-mfrs:NatureOfFinancialStatements': 'Separate',
  //     'ssmt-dei:DescriptionOfPresentationCurrency': 'MYR',
  //     'ssmt-dei:DescriptionOfFunctionalCurrency': 'MYR',
  //     'ssmt-dei:LevelOfRoundingUsedInFinancialStatements': 'Actuals',
  //     'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile':
  //       'MBRS_Preparation_Tool v2.2',
  //     'ssmt-dei:TaxonomyVersion': 'SSMxT_2022v1.0',
  //     'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange':
  //       'No',
  //     // Bank Negara field is auto-managed by setupBankNegaraFieldManagement() based on Type of Submission
  //     // Method fields are shown/hidden by setupFieldDisabling() based on Type of Submission (FS-* vs KFI-*)
  //     'ssmt:MethodUsedForPreparingStatementOfFinancialPosition':
  //       'Current-Noncurrent',
  //     'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss': 'Nature of expense',
  //     'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome': 'Before tax',
  //     'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity':
  //       'Statement of changes in equity',
  //     'ssmt:MethodUsedForPreparingStatementOfCashFlows': 'Indirect',
  //     'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated': 'No',
  //     'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister':
  //       'No',
  //   };

  //   this.filingForm.patchValue(demoData);
  //   this.filingForm.markAsDirty();

  //   this.messageService.add({
  //     severity: 'success',
  //     summary: 'Demo Data Loaded',
  //     detail: 'Form has been filled with sample data',
  //     life: 3000,
  //   });

  //   // Auto-determine standard
  //   this.determineStandardFromFormData();
  // }

  /**
   * Auto-determine MFRS/MPERS based on form data
   */
  private determineStandardFromFormData(): void {
    const statusOfCompany = this.filingForm.get(
      'ssmt-dei:StatusOfCompany'
    )?.value;
    const sharesTraded = this.filingForm.get(
      'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange'
    )?.value;
    const bankNegaraRegulated = this.filingForm.get(
      'ssmt:DisclosureOfWhetherCompanyRegulatedByBankNegaraMalaysiaAtFinancialYearEnd'
    )?.value;

    let determinedStandard = 'MPERS'; // Default
    let typeOfSubmission = 'FS-MPERS';

    // MFRS determination rules
    if (
      statusOfCompany === 'Public company' ||
      sharesTraded === 'Listed' ||
      bankNegaraRegulated === 'Company regulated by Bank Negara Malaysia'
    ) {
      determinedStandard = 'MFRS';
      typeOfSubmission = 'FS-MFRS';
    }

    // Update form
    this.filingForm.patchValue({
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements':
        determinedStandard,
      'ssmt-dei:TypeOfSubmission': typeOfSubmission,
    });
  }

  /**
   * Revert to saved state (original state from localStorage)
   */
  onRevert(): void {
    this.stateService.cancelSheetChanges(SHEET_KEYS.filingInfo);
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    // Re-finalize: loadSavedState() re-opened the initializingSheets
    // bracket via markSheetInitializing(); only finalizeInit() closes it.
    void this.finalizeInit();
    this.messageService.add({
      severity: 'info',
      summary: 'Changes Reverted',
      detail: 'Form reverted to last saved state',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Check if form has unsaved modifications
   */
  isModified(): boolean {
    return this.stateService.isSheetModified(SHEET_KEYS.filingInfo);
  }

  /**
   * Clear form data
   */
  clearForm(): void {
    this.filingForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.filingInfo);

    // Restore readonly default values
    this.filingForm.patchValue({
      'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile':
        'MBRS_Preparation_Tool v2.2',
      'ssmt-dei:TaxonomyVersion': 'SSMxT_2022v1.0',
    });

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All form data has been cleared',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Check if Role-1 is completed (has valid data with no errors)
   */
  private isRole1Completed(): boolean {
    const sheetLevelStatus = this.stateService.getSheetLevelStatus(SHEET_KEYS.filingInfo);
    const role1Data = this.stateService.getSheetData(SHEET_KEYS.filingInfo);
    const hasData = role1Data && Object.keys(role1Data).length > 0;
    return !sheetLevelStatus.hasErrors && hasData;
  }

  /**
   * Show edit warning dialog. Called when user tries to re-submit Role-1
   * after it was already completed — warns that downstream sheets may be
   * affected.
   */
  private showEditWarningAndClearData(): void {
    if (this.hasShownEditWarning) {
      return;
    }
    this.hasShownEditWarning = true;
    this.isEditWarningDialogOpen.set(true);
  }

  /**
   * Handle Convert button click in edit warning dialog
   */
  onEditWarningConvert(): void {
    this.isEditWarningDialogOpen.set(false);

    // Proceed with submission after user confirms. Commit is deferred to
    // onSubmitSucceeded() (container callback on a persisted response) — same
    // as the direct onSubmit() path — so a failed convert leaves the form dirty.
    const formData = this.filingForm.getRawValue();
    this.isSubmitting = true;
    this.submitForm.emit(formData);
  }

  /**
   * Handle Back button click in edit warning dialog
   */
  onEditWarningBack(): void {
    this.isEditWarningDialogOpen.set(false);
    // Reset warning flag to allow it to show again on next submit attempt
    this.hasShownEditWarning = false;
  }

  /**
   * Toggle edit warning dialog visibility
   */
  toggleEditWarningDialog(isOpen: boolean): void {
    this.isEditWarningDialogOpen.set(isOpen);
    if (!isOpen) {
      this.hasShownEditWarning = false;
    }
  }

  /**
   * Submit Role-1 form. Mirrors role0's onSubmit:
   * - Blocks an INVALID form (and surfaces its fields via markAllAsTouched)
   * - Blocks a form with NO unsubmitted changes — its own gate, measured
   *   modified-vs-original rather than by Angular's pristine flag
   * - Shows the edit-warning dialog when re-submitting an already-completed form
   * - Otherwise emits formData to parent and commits the sheet as original
   *
   * Both guards are silent; the grayed-out Submit button is the only
   * user-facing signal. Keep them in step with `[submitDisabled]` in the
   * template and with the parity cases in `filing-info-submit-gate-parity.spec.ts`.
   */
  onSubmit(): void {
    // SILENT defence-in-depth — see the matching block in role0's onSubmit.
    // The Submit button is the user-facing signal; these guards only stop a
    // non-button caller from emitting, and must mirror `[submitDisabled]` in
    // the template exactly. No toast: either could only fire on a path the
    // disabled button already blocks.
    //
    // Validity blocks, and surfaces the offending fields.
    if (!this.filingForm.valid) {
      this.filingForm.markAllAsTouched();
      return;
    }

    // "No changes detected" blocks too (P.O. ruling, 2026-08-17): Submit is
    // available only when the user has made changes AND those changes are
    // valid. This reverses 759f7d0, which dropped role1's pristine term so a
    // no-change Phase 2 re-submit stayed available.
    //
    // The term is the modified-vs-original verdict, NOT `filingForm.pristine` —
    // see the `hasUnsubmittedChanges` field for the two scenarios where the two
    // disagree and why this one is right in both.
    //
    // No `isOnlyFilingSheet` waiver here, unlike role0 — role1 has no such
    // input, and adding one would be actively harmful: the container clamps
    // `filteredSheets` to a single sheet once a sheet reports `hasErrors`,
    // so the flag flips true exactly when Submit must stay dead (0981ced).
    // markAllAsTouched is NOT called: a sheet with nothing to submit has no
    // errors to surface, and touching it would paint red borders on fields the
    // user never visited.
    if (!this.hasUnsubmittedChanges()) {
      return;
    }

    // Always route Submit through the confirmation dialog — first-time AND
    // re-submission. The backend request is fired only from the dialog's
    // Convert button (onEditWarningConvert), so nothing is submitted (and no
    // toast shows) until the user confirms in the pop-up. `hasShownEditWarning`
    // is re-armed after each submit outcome (onSubmitSucceeded/onSubmitFailed)
    // and on Back, so the dialog re-appears on the next Submit.
    if (!this.hasShownEditWarning) {
      this.showEditWarningAndClearData();
      return;
    }

    // Disable the button while the container's HTTP call is in flight, then
    // emit. Pristine/commit/completed bookkeeping is DEFERRED to
    // onSubmitSucceeded() (container callback on a persisted response) so a
    // failed submit leaves the form dirty and the Submit button re-enabled —
    // mirroring Submit-for-Validation.
    const formData = this.filingForm.getRawValue();
    this.isSubmitting = true;
    this.submitForm.emit(formData);
  }

  /**
   * Container callback: the phase-two submit persisted (HTTP 200, status true).
   * Only now do we commit local state and re-arm the edit-warning cycle.
   */
  onSubmitSucceeded(): void {
    this.isSubmitting = false;

    this.filingForm.markAsPristine();
    this.stateService.commitSheetAsOriginal(SHEET_KEYS.filingInfo);

    this.wasInitiallyCompleted = true;
    this.hasShownEditWarning = false;
  }

  /**
   * Container callback: the phase-two submit failed (non-200 / transport fault).
   * Stay on the form, re-enable the Submit button, do NOT commit local state.
   */
  onSubmitFailed(): void {
    this.isSubmitting = false;
    this.hasShownEditWarning = false;
  }

  /**
   * Cancel changes — reverts the form to its last-saved state and navigates
   * back to the XBRL files list. Confirms first if there are pending edits.
   */
  onCancel(): void {
    // Same changes-term as the Submit gate, and for the same reason: after the
    // user hand-reverts every field to its original value there is nothing to
    // cancel, so the "Any unsaved changes will be lost" prompt would be lying.
    // `filingForm.pristine` cannot see that revert (it never recovers once
    // dirtied) and is also reset by the container's *ngIf remount while edits
    // are still pending — which would leave Cancel dead with real work to
    // discard. See the `hasUnsubmittedChanges` field for both cases.
    if (!this.hasUnsubmittedChanges()) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Cancel Changes',
      message: 'Any unsaved changes will be lost. Do you want to continue?',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      closable: false,
      acceptButtonStyleClass: '!min-w-[80px] xlplus:!min-w-[100px] !text-sm xlplus:!text-base !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold',
      rejectButtonStyleClass: '!min-w-[80px] xlplus:!min-w-[100px] !border-taxpod-blue !text-taxpod-blue !border-[2px] !text-sm xlplus:!text-base hover:!bg-[#E0F2FE] !bg-white',
      accept: () => {
        this.stateService.cancelSheetChanges(SHEET_KEYS.filingInfo);
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        // Re-finalize: loadSavedState() re-opened the initializingSheets
        // bracket via markSheetInitializing(); only finalizeInit() closes
        // it.
        void this.finalizeInit();
        this.filingForm.markAsPristine();
        this.router.navigate(['/mbrs/xbrl-files']);
      },
      reject: () => {
        // Stay on page
      }
    });
  }

  /**
   * Handle Delete button — opens the delete-conversion dialog
   */
  onDelete(): void {
    this.isArchiveDialogOpen.set(true);
  }

  /**
   * Handle deletion completion from delete-conversion component
   */
  onDeletionCompleted(): void {
    this.stateService.clearAllData();

    this.router.navigate(['/mbrs/xbrl-files'], {
      state: {
        toast: {
          key: 'process_mbrs_data',
          severity: 'custom-success',
          summary: 'Conversion deleted successfully!',
          life: TOAST_DURATION_LONG_MS,
        },
      },
    });
  }

  /**
   * Get the DeleteConversionTarget for delete-conversion component
   * Creates a minimal conversion description from available inputs
   */
  get conversionData(): DeleteConversionTarget {
    return {
      id: this.conversionId || '',
      fileName: '',
      company: this.companyName || '',
      auditYear: this.auditYear?.toString() || '',
      status: this.conversionStatus ?? MbrsStatusEnum.InProgress,
      // Real values from the audit read, or undefined when none answered: the
      // dialog then shows "-" rather than an invented "now".
      conversionDate: this.conversionAudit?.conversionDate,
      updatedDate: this.conversionAudit?.updatedDate,
      createdBy: this.conversionAudit?.createdBy,
      updatedBy: this.conversionAudit?.updatedBy,
      fileUrl: '',
      mbrsData: [],
    };
  }

  /**
   * Get dropdown options for a field
   * Returns filtered options if available, otherwise returns all options
   */
  getDropdownOptions(enumSource?: string, qname?: string): any[] {
    if (!enumSource) return [];

    // Check if there are filtered options for this field
    if (qname && this.filteredDropdownOptions[qname]) {
      return this.filteredDropdownOptions[qname];
    }

    // Return all options
    const enumKey = enumSource.replace('type:', '');
    return this.dropdownOptions[enumKey] || [];
  }

  /**
   * Get static dropdown options
   */
  getStaticOptions(options?: string[]): any[] {
    if (!options) return [];
    return options.map((opt) => ({ label: opt, value: opt }));
  }

  /**
   * Check if field should be shown
   */
  shouldShowField(control: FormControlSchema): boolean {
    // Check if field is explicitly hidden in UI config
    if (control.ui.hidden === true) {
      return false;
    }

    // Check if field is hidden due to hideWhenDisabled
    if (this.hiddenFields[control.qname]) {
      return false;
    }

    // Check requiredIf condition
    if (control.requiredIf) {
      const dependentControl = this.filingForm.get(control.requiredIf.qname);
      return dependentControl?.value === control.requiredIf.equals;
    }

    // Check requiredIfAny condition
    if (control.requiredIfAny) {
      return control.requiredIfAny.some((condition) => {
        const dependentControl = this.filingForm.get(condition.qname);
        return dependentControl?.value === condition.equals;
      });
    }

    return true;
  }

  /**
   * Check if field is required
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory` so every sheet answers this the
   * same way. Each sheet used to hand-roll it and understood only the
   * declaration forms its own schema happened to use, which is how a mechanism
   * moved between sheets could silently stop marking. See `isControlMandatory` in `required-field.util.ts`.
   *
   * Mirrors role0 — see the readonly/`disabledWhen` note there. These two
   * sheets are duplicated engines; keep the pair identical.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, { selfData: this.filingForm.getRawValue() });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.filingForm.get(qname);
  }

  /**
   * Type 1: Check if field should be disabled (visible but read-only)
   * Returns true if field is disabled based on schema's disabledWhen configuration
   */
  shouldDisableField(fieldQname: string): boolean {
    const control = this.filingForm.get(fieldQname);
    return control ? control.disabled : false;
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden
   */
  shouldHideField(fieldQname: string): boolean {
    return this.hiddenFields[fieldQname] === true;
  }

  /**
   * Type 3: Check if field should be shown in specific section
   * Role-1 has single section, so always return true
   */
  shouldShowInSection(fieldQname: string, sectionId: string): boolean {
    return true; // Role-1 has no section-based hiding
  }

  /**
   * retrieve-parent-sheets here
   * Note: Data is fetched by filing-form-container and populated via FilingFormStateService
   * This method can be used to manually reload FilingInfo data from the state service
   */
  private reloadFilingInfoFromState(): void {
    const filingInfoData = this.stateService.getSheetData(SHEET_KEYS.filingInfo);

    // If you want to manually trigger a reload from the data:
    if (filingInfoData && Object.keys(filingInfoData).length > 0) {
      // emitEvent: false — this is currently unreferenced, but if revived it
      // would otherwise fire the driver-clear cascade and the lockWhen /
      // conditional-options subscribers mid-hydration: an object-key-order
      // dependent patch could transiently present Origin/Types-of-company as
      // "cleared" and wipe Basis via setupDriverClearCascade. role0 has no
      // counterpart method to carry the same hazard.
      this.filingForm.patchValue(filingInfoData, { emitEvent: false });
      this.messageService.add({
        severity: 'success',
        summary: 'Data Loaded',
        detail: 'FilingInfo populated from backend',
        life: TOAST_DURATION_SHORT_MS,
      });
    }
    else {
      this.messageService.add({
        severity: 'info',
        summary: 'No Data',
        detail: 'No FilingInfo data available in state',
        life: TOAST_DURATION_SHORT_MS,
      });
    }
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Layer 1: local valueChanges — debounced re-evaluation when the user edits.
    this.filingForm.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });

    // Layer 2: cross-tab live adoption — when a sibling tab edits Phase 2's
    // filingInfo slice, `applyRemoteChange` rehydrates the shared state and
    // publishes `changedSheets` on `remoteUpdateApplied$`. `adoptRemoteState()`
    // below repaints this sheet's form from that state (silently, no emitted
    // valueChanges), which is what makes Layer 1's local valueChanges-driven
    // re-evaluation moot here — the field-level warning indicators are
    // refreshed as part of adoption instead. Mirrors role0's Layer 2 (which
    // pins filingPrerequisites); here streamKey is 'modifiedState' and
    // changedSheets carries filingInfo.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.filingInfo)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.filingInfo)),
        filter(() => !!this.schema && !!this.filingForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role1-filingInfo',
          // });
        }
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    // Date fields must be coerced BEFORE the patch, exactly as `loadSavedState()`
    // and the `originalState$` listener already do. A sibling's DISCARD
    // republishes the ORIGINAL slice — which carries the API's raw
    // "dd/MM/yyyy" strings, not the `Date` objects the modified slice holds —
    // so adopting it verbatim wrote a string into `<p-datepicker>`, which
    // renders an unparseable value as an EMPTY field. Reported as the two
    // "Company previous financial year" dates staying blank after a sibling
    // discarded, while the dropdowns on the same sheet reverted correctly.
    // Pinned by `cross-tab-adoption-date-coercion.spec.ts`.
    adoptIntoAutoSaveSheet(
      this.filingForm,
      this.convertDateStringsToDateObjects(
        this.stateService.getSheetData(SHEET_KEYS.filingInfo)
      )
    );
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.filingForm) return;

    // Get all form values for cross-field validations
    const allFormValues = this.filingForm.getRawValue();

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control: FormControlSchema) => {
      if ((control as any).warnings && (control as any).warnings.length > 0) {
        const formControl = this.filingForm.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            (control as any).warnings,
            allFormValues,
            true // Always evaluate warnings
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          }
          else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Resolve the display error message for a field + error type. Prefers the
   * per-field custom copy declared in role-1.form.json (`control.error`),
   * falling back to a generic message. Mirrors role0 so both filing-info
   * surfaces show identical, schema-driven validation copy.
   */
  getErrorMessage(control: FormControlSchema, errorType: 'required' | 'pattern'): string {
    // Check if custom error message is defined in schema
    if (control.error && control.error[errorType]) {
      return control.error[errorType]!;
    }

    // Return default error messages
    if (errorType === 'required') {
      return control.error?.required ?? `${control.label} is required`;
    }

    if (errorType === 'pattern') {
      return control.error?.pattern ?? 'Invalid format';
    }

    return 'Validation error';
  }

  /**
   * Handle input event with keystroke-level filtering + maxLength enforcement.
   * Delegates to the shared utils/input-filter helper. Mirrors role0 so both
   * filing-info surfaces sanitise text input identically (e.g. the numeric,
   * 12-char company registration number).
   */
  applyInputFilter = (event: Event, filterType: string | undefined, maxLength: number | undefined, qname: string, trimLeadingZeros?: boolean): void => {
    applyInputFilter(event, filterType, maxLength, qname, this.filingForm, trimLeadingZeros);
  };

  /**
   * Handle paste event with input filtering. Mirrors role0.
   */
  onPasteFilter = (event: ClipboardEvent, filterType: string | undefined, maxLength: number | undefined, qname: string, trimLeadingZeros?: boolean): void => {
    applyPasteFilter(event, filterType, maxLength, qname, this.filingForm, trimLeadingZeros);
  };

  /**
   * Handle typed (keyboard) entry into a date picker input. PrimeNG's
   * DatePicker does not push a partial/typed "dd/MM/yyyy" value onto the
   * reactive control until it parses a complete date, so without this hook
   * the cross-field financial-year validators would not re-run on keyboard
   * entry (only on calendar pick). Delegates to the shared helper which
   * parses dd/MM/yyyy and patches the control. Mirrors role0.
   */
  onInputChange(event: InputEvent): void {
    handleDateInputChange(event, this.filingForm);
  }

  /** A mouse paste never reaches PrimeNG's model on its own — see `handleDatePaste`. */
  onDatePaste(event: ClipboardEvent): void {
    handleDatePaste(event, this.filingForm);
  }
}
